import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Collapse ,
  InputAdornment,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Science as ScienceIcon,
  Timeline as TimelineIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  AutoAwesome as AutoAwesomeIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  TableChart as TableChartIcon,
  Visibility as VisibilityIcon,
  Code as CodeIcon,
  AddCircleOutline as AddRowIcon,
  RemoveCircleOutline as RemoveRowIcon,
  InfoOutlined as InfoIcon,
  PriceChange as PriceChangeIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { loadHaematologyParameters, addLabCategory, loadLabCategories,
   deleteLabCategory, updateLabCategory, addLabParameters, updateLabParameter,
   updateLabParameterGroup, generateLabParameters, generateLabInterpretation,
   deleteLabParameter,
   getLabInsurances, loadInsurance,
   facilitySettingsAPI,
   labPriceChangeRequestCreate,
} from '../BaseURL';
import Swal from 'sweetalert2';
import { AccessTime } from '@mui/icons-material';
import { extractIdentifiers, FormulaError, parse as parseFormula, findCycles,
  evaluate as evalFormula, parseRange, formatDerived } from './formulaEval';
import { generateLabReportHTML } from './LabReportTemplate';
import { generateLabReportHTMLDekyi } from './LabReportTemplateDekyi';

// ── Patient-context identifiers ──────────────────────────────────────────
// Names that are auto-injected into a derived formula's scope at
// result-entry time by `buildPatientScope()` in MyPatients.js. They're
// legal in formulas even though they're NOT declared as parameters on
// the lab — clinical formulas like eGFR (CKD-EPI) and Cockcroft-Gault
// genuinely need the patient's age, and forcing the technician to add
// an "Age" parameter to the panel would be silly. Keep this list in
// sync with `buildPatientScope()`: if you expose a new field there,
// list it here too so the setup-time validator accepts it.
const PATIENT_CONTEXT_IDS = new Set([
  'age', 'ageyears', 'age_years', 'agemonths', 'age_months',
]);

// ── Deduplicate parameter rows that differ only in their flag value ──
// The DB stores one row per flag (so a parameter with 20 flags appears 20
// times), which inflates the visible count and makes the lab look noisy.
// This collapses rows that share the same test/ranges/unit into a single
// row whose `flag` is the comma-joined union of all the originals. The
// first row's trans_code/cat are kept so Edit/Delete still target a real
// record; `_originals` holds every underlying row in case a caller needs
// to act on all of them.
const dedupeParameters = (params) => {
  if (!Array.isArray(params) || params.length === 0) return [];
  const norm = (v) => String(v ?? '').trim().toLowerCase();
  const byKey = new Map();
  for (const p of params) {
    const key = [
      norm(p.test), norm(p.male), norm(p.female),
      norm(p.children), norm(p.neonates), norm(p.unit),
    ].join('|');
    const flags = String(p.flag ?? '')
      .split(',').map((f) => f.trim()).filter(Boolean);
    if (!byKey.has(key)) {
      byKey.set(key, { ...p, _flagSet: new Set(flags), _originals: [p] });
    } else {
      const existing = byKey.get(key);
      flags.forEach((f) => existing._flagSet.add(f));
      existing._originals.push(p);
    }
  }
  return Array.from(byKey.values()).map(({ _flagSet, ...rest }) => ({
    ...rest,
    flag: Array.from(_flagSet).join(', '),
  }));
};

// ── Interpretation table helpers ─────────────────────────────────────────
const parseInterpHTML = (html) => {
  if (!html || !html.trim()) return { comment: '', headers: [], rows: [] };
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const pEl = doc.querySelector('p');
    let rawComment = '';
    if (pEl) {
      // Convert <br> tags to newlines before reading text so multi-line
      // comments round-trip correctly through the visual editor.
      pEl.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
      rawComment = pEl.textContent.trim();
    }
    const comment = rawComment.replace(/^Comments?:\s*/i, '');
    const table = doc.querySelector('table');
    if (!table) return { comment, headers: [], rows: [] };
    const allRows = Array.from(table.querySelectorAll('tr'));
    if (!allRows.length) return { comment, headers: [], rows: [] };
    const headers = Array.from(allRows[0].querySelectorAll('th,td')).map(c => c.textContent.trim());
    const rows = allRows.slice(1).map(tr =>
      Array.from(tr.querySelectorAll('td,th')).map(c => c.textContent.trim())
    );
    return { comment, headers, rows };
  } catch (_) { return { comment: '', headers: [], rows: [] }; }
};

const buildInterpHTML = ({ comment, headers, rows }) => {
  // Multi-line comment: each \n becomes a <br> so the structure is preserved.
  const commentLine = comment.trim()
    ? `<p style="margin:0 0 6px 0;font-size:8.5pt;"><strong>Comments:</strong> ${comment.trim().replace(/\n/g, '<br>')}</p>\n`
    : '';
  if (!headers.length && !rows.length) return commentLine.trim();
  // First column is the interpretation label — left-aligned.
  // Every other column (markers) is centered.
  const thHTML = headers.map((h, i) =>
    `<th style="border:1px solid #aab4c8;padding:3px 6px;font-weight:bold;${i === 0 ? 'text-align:left;' : 'text-align:center;'}">${h}</th>`
  ).join('');
  const tbodyHTML = rows.map(row => {
    const cells = headers.map((_, i) => row[i] ?? '');
    return `<tr>${cells.map((c, i) =>
      `<td style="border:1px solid #aab4c8;padding:3px 6px;${i === 0 ? 'text-align:left;' : 'text-align:center;'}">${c}</td>`
    ).join('')}</tr>`;
  }).join('\n');
  return `${commentLine}<table style="width:100%;border-collapse:collapse;font-size:8pt;">\n<thead><tr>${thHTML}</tr></thead>\n<tbody>\n${tbodyHTML}\n</tbody>\n</table>`;
};

const Home = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [haematologyData, setHaematologyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  // Default to 15 rows now that the cards fill the full viewport — fits
  // comfortably without forcing the user to page through small chunks.
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [formData, setFormData] = useState([{
    id: Date.now(),
    // Existing trans_codes from DB (all flag-rows of this logical parameter).
    // Empty for new rows. Populated when the user loads an existing lab to edit.
    trans_codes: [],
    parameterName: '',
    type: '',
    maleRange: '',
    femaleRange: '',
    childrenRange: '',
    neonateRange: '',
    unit: '',
    // Dual-unit support — both optional. When both are filled, the parameter
    // accepts results in either unit and converts via:
    //   value_in_alt = value_in_primary × conversionFactor
    altUnit: '',
    conversionFactor: '',
    flags: ''
  }]);
  const [selectedCategory, setSelectedCategory] = useState('');
  // Result-entry format for the currently-edited lab. Applies to the
  // whole hold_show group, not per-row — every row sent in the save
  // payload uses this value. Defaults to 'parameter' (the legacy
  // behaviour); flip to 'narrative' for descriptive labs (peripheral
  // blood smear etc.) where each row is a section title + paragraph,
  // there's no range/unit/formula, and the report renders the narrative
  // template.
  const [labFormat, setLabFormat] = useState('parameter');
  // Name of the lab currently loaded into the dialog (or '' when starting
  // fresh). Used both for the Autocomplete display value and the "Editing
  // …" banner copy below.
  const [loadedLabName, setLoadedLabName] = useState('');
  // While true, the dialog renders a translucent overlay with a spinner so
  // the user gets visual feedback that the lab they clicked is being
  // prepared — important when a lab has 20+ parameters and the mount of
  // every parameter row takes a tick or two.
  const [loadingLabGroup, setLoadingLabGroup] = useState(false);
  // ── AI parameter-generation state ──
  // The user opens this small dialog from inside Add Lab Parameters, types
  // the name of a lab they want to set up (and optionally a few hints), and
  // the backend asks DeepSeek to return a complete parameter set. The
  // returned rows are pushed straight into formData so the user can review
  // and save without retyping anything.
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiForm, setAiForm] = useState({
    labName: '',
    specimenType: '',
    methodology: '',
    ageFocus: '',
    region: 'Ghana / West Africa',
    extraNotes: '',
  });
  const [aiError, setAiError] = useState('');
  const [categoryFormData, setCategoryFormData] = useState({
    categoryName: '',
    waitingTime: '',
    interpretation: ''
  });
  const [categories, setCategories] = useState([]);

  // ── Removed-row tracker for the editor dialog ────────────────────────
  // When the user loads an existing lab into the editor and then removes
  // one of its parameter rows, we stash the trans_codes here. handleSave
  // sends them to /deleteLabParameter so the DB stays in sync. Without
  // this, the remove button was a no-op for the backend and the deleted
  // parameter kept reappearing after refresh.
  const [removedTransCodes, setRemovedTransCodes] = useState([]);

  // ── Lab preview state ───────────────────────────────────────────────
  // The eye icon on every lab row opens this dialog with a fake report
  // rendered from dummy values — so the user can see exactly how the
  // standard and branded layouts will look once real results are entered.
  const [previewOpen, setPreviewOpen]         = useState(false);
  const [previewGroup, setPreviewGroup]       = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState('branded');
  const [previewHtml, setPreviewHtml]         = useState('');

  const [editingParameter, setEditingParameter] = useState(null);
  const [editParameterFormData, setEditParameterFormData] = useState({
    parameterName: '',
    type: 'independent',
    maleRange: '',
    femaleRange: '',
    childrenRange: '',
    neonateRange: '',
    unit: '',
    altUnit: '',
    conversionFactor: '',
    isDerived: false,
    formula: '',
    flags: ''
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryFormData, setEditCategoryFormData] = useState({
  categoryName: '',
  waitingTime: '',
  interpretation: ''
});

  // Interpretation table dialog
  const [interpDialogCategory, setInterpDialogCategory] = useState(null);
  const [interpDialogValue, setInterpDialogValue]       = useState('');
  const [interpDialogSaving, setInterpDialogSaving]     = useState(false);
  const [interpEditMode, setInterpEditMode]             = useState('visual'); // 'visual' | 'html' | 'preview'
  const [interpTableData, setInterpTableData]           = useState({ comment: '', headers: [], rows: [] });
  const [interpAiGenerating, setInterpAiGenerating]     = useState(false);
  const [interpAiError, setInterpAiError]               = useState('');

  // Pagination and filtering states
  const [categoryPage, setCategoryPage] = useState(0);
  const [categoryRowsPerPage, setCategoryRowsPerPage] = useState(15);
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [labSearchTerm, setLabSearchTerm] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');

  // ── Lab pricing info + change-request state ──────────────────────────
  // The info icon on each Lab Parameters row opens a read-only dialog
  // showing every insurance partner's tariff for that lab (+ top-up,
  // uninsured, G-DRG). If a price isn't set the row is flagged "not
  // set" — and the lab tech can click "Request change" to ask admin to
  // add or update one. Admin reviews the request from their own panel;
  // nobody at the lab desk edits `lab_prices` directly.
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [infoLabName,    setInfoLabName]    = useState('');
  const [infoRows,       setInfoRows]       = useState([]);
  const [infoLoading,    setInfoLoading]    = useState(false);

  // Insurance partner list — loaded once and reused for the change-request
  // dropdown. Failing silently → keep the dropdown empty; the technician
  // can still file a request against "uninsured / general".
  const [insurancePartners, setInsurancePartners] = useState([]);

  // Whether the info icon renders at all. Driven by the facility-level
  // `lab_price_info_enabled` toggle (admin → Module Toggles). Default ON
  // so a missing setting doesn't accidentally hide the feature.
  const [labPriceInfoEnabled, setLabPriceInfoEnabled] = useState(true);

  // Nested "request a price change" dialog state.
  const [reqOpen,      setReqOpen]      = useState(false);
  const [reqInsurance, setReqInsurance] = useState('');
  const [reqCurrent,   setReqCurrent]   = useState(null);
  const [reqAmount,    setReqAmount]    = useState('');
  const [reqReason,    setReqReason]    = useState('');
  const [reqSaving,    setReqSaving]    = useState(false);

  const currentUserLabel = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('userInfo') || '{}');
      return u?.name || u?.username || '';
    } catch (_) { return ''; }
  })();

  // Fetch haematology parameters on component mount
  useEffect(() => {
    fetchHaematologyParameters();
    fetchCategories();
  }, []);

  // Read the facility toggle once at mount. Quiet failure → leave ON.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(facilitySettingsAPI, { timeout: 8000 });
        if (!alive) return;
        const row = data?.success && Array.isArray(data.results)
          ? data.results.find((r) => r.key === 'lab_price_info_enabled')
          : null;
        if (row) {
          const s = String(row.value ?? '').trim().toLowerCase();
          setLabPriceInfoEnabled(!(s === 'false' || s === '0' || s === 'off' || s === 'no'));
        }
      } catch (_) { /* keep default ON */ }
    })();
    return () => { alive = false; };
  }, []);

  // Insurance partners — used only by the request dialog. Loaded lazily
  // the first time the info dialog opens to avoid an extra request on
  // every page mount.
  const loadInsurancePartnersOnce = async () => {
    if (insurancePartners.length > 0) return;
    try {
      const res = await axios.get(loadInsurance);
      if (res.data?.success) setInsurancePartners(res.data.results || []);
    } catch (_) { /* dropdown stays empty */ }
  };

  const openLabInfo = async (labName) => {
    setInfoLabName(labName);
    setInfoRows([]);
    setInfoDialogOpen(true);
    setInfoLoading(true);
    loadInsurancePartnersOnce();
    try {
      const res = await axios.post(getLabInsurances, { categoryName: labName });
      setInfoRows(res.data?.success ? (res.data.results || []) : []);
    } catch (_) {
      setInfoRows([]);
    } finally {
      setInfoLoading(false);
    }
  };

  const openRequestChange = (row = null) => {
    setReqInsurance(row?.status || '');
    setReqCurrent(row && Number.isFinite(Number(row.cost)) ? Number(row.cost) : null);
    setReqAmount(row?.cost != null ? String(row.cost) : '');
    setReqReason('');
    setReqOpen(true);
  };

  const submitChangeRequest = async () => {
    if (!infoLabName) return;
    const amt = Number(reqAmount);
    if (!Number.isFinite(amt) || amt < 0) {
      await Swal.fire({
        icon: 'warning',
        title: 'Enter a valid amount',
        text: 'Proposed amount must be a non-negative number.',
      });
      return;
    }
    setReqSaving(true);
    try {
      const { data } = await axios.post(labPriceChangeRequestCreate, {
        lab:           infoLabName,
        insurance:     reqInsurance || null,
        currentCost:   reqCurrent,
        requestedCost: amt,
        reason:        reqReason || null,
        requestedBy:   currentUserLabel || null,
      });
      if (data?.success) {
        setReqOpen(false);
        await Swal.fire({
          icon: 'success',
          title: 'Request sent',
          text: 'Your price-change request has been logged for admin review.',
          timer: 2200, showConfirmButton: false,
        });
      } else {
        await Swal.fire({ icon: 'error', title: 'Could not submit', text: data?.message || 'Try again.' });
      }
    } catch (err) {
      await Swal.fire({ icon: 'error', title: 'Could not submit', text: err.response?.data?.message || err.message });
    } finally {
      setReqSaving(false);
    }
  };

  const fetchHaematologyParameters = async () => {
    try {
      setLoading(true);
      const response = await axios.get(loadHaematologyParameters);
      if (response.data.success) {
        setHaematologyData(response.data.results);
      }
    } catch (error) {
      console.error('Error fetching haematology parameters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeCategoryPage = (event, newPage) => {
    setCategoryPage(newPage);
  };

  const handleChangeCategoryRowsPerPage = (event) => {
    setCategoryRowsPerPage(parseInt(event.target.value, 10));
    setCategoryPage(0);
  };

  const toggleRowExpansion = (groupIndex) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(groupIndex)) {
      newExpandedRows.delete(groupIndex);
    } else {
      newExpandedRows.add(groupIndex);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    // Reset form
    setFormData([{
      id: Date.now(),
      trans_codes: [],
      parameterName: '',
      type: 'independent',
      maleRange: '',
      femaleRange: '',
      childrenRange: '',
      neonateRange: '',
      unit: '',
      altUnit: '',
      conversionFactor: '',
      isDerived: false,
      formula: '',
      flags: ''
    }]);
    setSelectedCategory('');
    setLoadedLabName('');
    setLoadingLabGroup(false);
    setLabFormat('parameter');
  };

  const handleInputChange = (parameterId, field, value) => {
    setFormData(prev => prev.map(param =>
      param.id === parameterId
        ? { ...param, [field]: value }
        : param
    ));
  };

  const addParameterRow = () => {
    const newParameter = {
      id: Date.now(),
      trans_codes: [],
      parameterName: '',
      type: 'independent',
      maleRange: '',
      femaleRange: '',
      childrenRange: '',
      neonateRange: '',
      unit: '',
      altUnit: '',
      conversionFactor: '',
      isDerived: false,
      formula: '',
      flags: ''
    };
    // Append at the bottom so the new row sits right below the user's last
    // entry and the inline "Add another parameter" tile.
    setFormData(prev => [...prev, newParameter]);
  };

  const removeParameterRow = (parameterId) => {
    if (formData.length <= 1) return;
    // Capture the trans_codes of the row being removed BEFORE we drop it
    // from formData. If the row was loaded from the DB (has trans_codes),
    // we need to remember those so the save handler can DELETE them — the
    // save path otherwise only walks the current formData and would leave
    // orphan rows in the haematology table.
    const target = formData.find(p => p.id === parameterId);
    const orphanTransCodes = Array.isArray(target?.trans_codes) ? target.trans_codes : [];
    if (orphanTransCodes.length > 0) {
      setRemovedTransCodes(prev => [...prev, ...orphanTransCodes]);
    }
    setFormData(prev => prev.filter(param => param.id !== parameterId));
  };

  // ─── Reorder helpers ─────────────────────────────────────────────────
  // The array index is the source of truth for ordering — on save we send
  // the index as `displayOrder` so the backend persists the same sequence
  // on the haematology rows. Result entry and the report read the same
  // column back out, so this single source flows through everywhere.
  const moveParameterUp = (parameterId) => {
    setFormData((prev) => {
      const idx = prev.findIndex((p) => p.id === parameterId);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };
  const moveParameterDown = (parameterId) => {
    setFormData((prev) => {
      const idx = prev.findIndex((p) => p.id === parameterId);
      if (idx === -1 || idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };

  // ─── Load existing lab into the dialog for bulk editing ──────────────
  // The user picks an existing lab from the dropdown at the top of the
  // dialog. We dedupe the per-flag rows back into one logical row per
  // parameter, carry the full trans_codes array on each row so we can
  // wipe-and-rewrite on save, and pre-fill every editable field.
  //
  // Wrapped in setLoadingLabGroup so the dialog can show a spinner overlay
  // for large labs — dedupeParameters + mount of 20+ TextField rows can
  // briefly block the main thread, and without feedback the user thinks
  // their click did nothing.
  const loadLabIntoForm = (labName) => {
    if (!labName) return;
    const group = haematologyData.find(g => g.hold_show === labName);
    if (!group) return;

    setLoadingLabGroup(true);
    // Defer so the spinner has a chance to paint before we do the heavy
    // synchronous work of mapping & state-updating every row.
    setTimeout(() => {
      try {
        const deduped = dedupeParameters(group.parameters);
        const cat = group.parameters[0]?.cat || labName;

        const rows = deduped.map((p, idx) => ({
          // Unique React key — using the first trans_code keeps it stable
          // across edits; falls back to a synthetic id for safety.
          id: (p._originals?.[0]?.trans_code) || (Date.now() + idx),
          trans_codes: Array.isArray(p._originals)
            ? p._originals.map(o => o.trans_code).filter(Boolean)
            : (p.trans_code ? [p.trans_code] : []),
          parameterName:    p.test || '',
          type:             p.is_derived ? 'dependent' : (p.type || 'independent'),
          maleRange:        p.male || '',
          femaleRange:      p.female || '',
          childrenRange:    p.children || '',
          neonateRange:     p.neonates || '',
          unit:             p.unit || '',
          altUnit:          p.alt_unit || '',
          conversionFactor: (p.unit_factor === null || p.unit_factor === undefined)
            ? ''
            : String(p.unit_factor),
          isDerived:        !!p.is_derived,
          formula:          p.formula || '',
          flags:            p.flag || '',
        }));

        setSelectedCategory(cat);
        setLoadedLabName(labName);
        // Pick up the lab's result-entry format from the catalog. Every
        // row of a hold_show group shares the same value so the first
        // parameter row is authoritative. Falls back to 'parameter' for
        // pre-migration rows that don't carry the column.
        setLabFormat(group.parameters[0]?.lab_format === 'narrative' ? 'narrative' : 'parameter');
        setRemovedTransCodes([]); // fresh edit session — drop any stale orphans
        setFormData(rows.length > 0 ? rows : [{
          id: Date.now(),
          trans_codes: [],
          parameterName: '', type: '',
          maleRange: '', femaleRange: '', childrenRange: '', neonateRange: '',
          unit: '', altUnit: '', conversionFactor: '', flags: ''
        }]);
      } finally {
        // Hide the spinner on the next tick so the freshly-rendered rows
        // appear at the same time the overlay fades out.
        setTimeout(() => setLoadingLabGroup(false), 0);
      }
    }, 0);
  };

  // Drops the loaded lab and clears the rows back to a single empty row.
  // Lets the user bail out of edit mode without closing the dialog.
  const resetEditingLab = () => {
    setLoadedLabName('');
    setSelectedCategory('');
    setLoadingLabGroup(false);
    setRemovedTransCodes([]);
    setFormData([{
      id: Date.now(),
      trans_codes: [],
      parameterName: '', type: 'independent',
      maleRange: '', femaleRange: '', childrenRange: '', neonateRange: '',
      unit: '', altUnit: '', conversionFactor: '',
      isDerived: false, formula: '',
      flags: ''
    }]);
  };

  // ─── AI parameter generation ─────────────────────────────────────────
  // Posts the user's lab name (plus any optional context) to the backend,
  // which calls DeepSeek and returns a shaped parameter set. The returned
  // rows are pushed straight into formData with trans_codes=[] so the
  // existing "save" path treats them as new inserts.
  const handleOpenAiDialog = () => {
    setAiError('');
    setAiForm((prev) => ({
      ...prev,
      // Seed the lab name from whatever the user might have already typed
      // as the loaded lab — saves a step in the common "I want this lab
      // but it's not in the system yet" flow.
      labName: prev.labName || '',
    }));
    setAiDialogOpen(true);
  };

  const handleCloseAiDialog = () => {
    if (aiGenerating) return; // don't let the user dismiss mid-request
    setAiDialogOpen(false);
    setAiError('');
  };

  const handleGenerateWithAi = async () => {
    const labName = String(aiForm.labName || '').trim();
    if (!labName) {
      setAiError('Please enter the name of the lab.');
      return;
    }
    setAiGenerating(true);
    setAiError('');
    try {
      const { data } = await axios.post(generateLabParameters, {
        labName,
        specimenType: aiForm.specimenType,
        methodology:  aiForm.methodology,
        ageFocus:     aiForm.ageFocus,
        region:       aiForm.region,
        extraNotes:   aiForm.extraNotes,
      });
      if (!data?.success) {
        throw new Error(data?.message || 'The AI did not return a usable response.');
      }
      const payload = data.data || {};
      const params  = Array.isArray(payload.parameters) ? payload.parameters : [];
      if (!params.length) {
        throw new Error('The AI returned no parameters. Try adding more context and retry.');
      }

      // Shape the returned objects into the formData row contract. We mark
      // trans_codes as empty so the bulk insert path is used on save.
      const rows = params.map((p, idx) => ({
        id: Date.now() + idx,
        trans_codes: [],
        parameterName:    p.name || '',
        type:             p.isDerived ? 'dependent' : (p.type || 'independent'),
        maleRange:        p.maleRange || '',
        femaleRange:      p.femaleRange || '',
        childrenRange:    p.childrenRange || '',
        neonateRange:     p.neonateRange || '',
        unit:             p.unit || '',
        altUnit:          p.altUnit || '',
        conversionFactor: (p.conversionFactor === null || p.conversionFactor === undefined)
          ? ''
          : String(p.conversionFactor),
        isDerived:        !!p.isDerived,
        formula:          p.formula || '',
        flags:            p.flags || '',
      }));

      // Pre-fill the category if the AI suggested one and the user hasn't
      // already picked one — but don't overwrite an explicit choice.
      if (payload.category && !selectedCategory) {
        setSelectedCategory(payload.category);
      }
      setLoadedLabName(''); // AI rows are new — clear any "editing existing" mode
      setFormData(rows);
      setAiDialogOpen(false);
      setAiForm((prev) => ({ ...prev, labName: '' }));
      Swal.fire({
        icon: 'success',
        title: 'AI generated parameters',
        text: `${rows.length} parameter${rows.length === 1 ? '' : 's'} loaded — review and save.`,
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Generation failed.';
      setAiError(msg);
    } finally {
      setAiGenerating(false);
    }
  };

  // Opens the Add Lab Parameters dialog with a specific lab already
  // pre-loaded. Wired to the edit pencil on every test-group row in the
  // Lab Parameters list, so the user can jump straight from "I want to
  // edit this lab" to the editor without typing the lab name.
  //
  // Flips the loading spinner BEFORE opening the dialog so the very first
  // paint already shows "loading" — important for big labs where the
  // dedupe + mount delay would otherwise look like a frozen click.
  const handleEditLabGroup = (labName) => {
    setLoadingLabGroup(true);
    setOpenDialog(true);
    // Defer the load until after the dialog has mounted so the
    // Autocomplete picks up the new value cleanly. loadLabIntoForm
    // resets the spinner when it's finished.
    setTimeout(() => loadLabIntoForm(labName), 0);
  };

  // Deletes every parameter row (across all flags) that belongs to a lab
  // group. Asks for confirmation first so a mis-click can't wipe a lab.
  const handleDeleteLabGroup = async (group) => {
    const dedupedCount = dedupeParameters(group.parameters).length;
    const totalRows = group.parameters.length;
    const result = await Swal.fire({
      title: `Delete "${group.hold_show}"?`,
      html: `This will permanently remove <b>${dedupedCount}</b> parameter${dedupedCount === 1 ? '' : 's'} ` +
            `(${totalRows} flag row${totalRows === 1 ? '' : 's'}) from this lab.<br/><br/>` +
            `<span style="color:#d33;font-weight:600;">This action cannot be undone.</span>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#11998e',
      confirmButtonText: 'Yes, delete the entire lab',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    Swal.fire({
      title: 'Deleting…',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading(),
    });

    let deleted = 0;
    const failures = [];
    for (const p of group.parameters) {
      try {
        await axios.delete(deleteLabParameter(p.trans_code));
        deleted++;
      } catch (err) {
        failures.push({ test: p.test, msg: err.response?.data?.message || err.message });
      }
    }
    Swal.close();

    if (failures.length === 0) {
      await Swal.fire({
        title: 'Deleted',
        text: `Removed ${deleted} row${deleted === 1 ? '' : 's'} from "${group.hold_show}".`,
        icon: 'success',
        timer: 1800,
        showConfirmButton: false,
      });
    } else {
      await Swal.fire({
        title: 'Partially deleted',
        text: `Removed ${deleted} row${deleted === 1 ? '' : 's'}; ${failures.length} failed.`,
        icon: 'warning',
        confirmButtonColor: '#d33',
      });
    }
    fetchHaematologyParameters();
  };

  // ─── Lab preview — dummy-data report ──────────────────────────────────
  // Build a fake `reportData` payload for the lab so the existing
  // generateLabReportHTML / generateLabReportHTMLDekyi renderers can show
  // the user exactly what the printed report will look like once real
  // results are entered. Numeric ranges pick the midpoint; open-bounds use
  // a safe value inside the bound; qualitative parameters default to the
  // "Negative" / first-listed flag.
  const buildDummyReportData = (group) => {
    if (!group || !Array.isArray(group.parameters)) return {};
    const params = dedupeParameters(group.parameters);

    // Narrative-lab preview: each section gets a placeholder paragraph
    // so the user can see the descriptive layout populated. The report
    // template renders these rows differently from parameter rows.
    const isNarrative = group.parameters?.[0]?.lab_format === 'narrative';
    if (isNarrative) {
      const today = new Date().toISOString().slice(0, 10);
      const sectionRows = params.map((p) => ({
        parameter:  p.test,
        result:     `Sample finding for ${p.test}. Replace with the tech's actual observation when results are entered.`,
        result_alt: '',
        unit:       '',
        unit_alt:   '',
        range:      '',
        flag:       'normal',
        impression: 'Sample clinical impression — the full conclusion line will appear here.',
      }));
      return { [today]: { [group.hold_show]: sectionRows } };
    }

    const fmtNum = (n) => {
      const abs = Math.abs(n);
      if (abs >= 100) return n.toFixed(1);
      if (abs >= 1)   return n.toFixed(2);
      return n.toFixed(3);
    };

    const scope = {}; // for derived formula evaluation
    const independents = params.filter(p => !p.is_derived);
    const derived      = params.filter(p =>  p.is_derived);

    const results = [];

    for (const p of independents) {
      const rangeStr = p.male || p.female || p.children || p.neonates || '';
      const parsed   = parseRange(rangeStr);
      let value, flag;

      if (parsed) {
        const { lo, hi } = parsed;
        if (lo != null && hi != null)       value = fmtNum((lo + hi) / 2);
        else if (lo != null)                value = fmtNum(lo * 1.1);
        else if (hi != null)                value = fmtNum(hi * 0.7);
        else                                value = '—';
        flag = 'Normal';
        const num = parseFloat(value);
        if (Number.isFinite(num)) scope[p.test] = num;
      } else {
        // Qualitative: pick the safest flag from the allowed list
        const flagsLc = String(p.flag || '').toLowerCase();
        if (flagsLc.includes('negative'))      { value = 'Negative';     flag = 'Negative'; }
        else if (flagsLc.includes('positive')) { value = 'Negative';     flag = 'Negative'; }
        else if (flagsLc.includes('normal'))   { value = 'Within range'; flag = 'Normal'; }
        else                                   { value = '—';            flag = '—'; }
      }

      results.push({
        parameter:  p.test,
        result:     value,
        result_alt: '',
        unit:       p.unit || '',
        unit_alt:   '',
        range:      rangeStr || '—',
        flag,
      });
    }

    for (const p of derived) {
      const rangeStr = p.male || p.female || p.children || p.neonates || '';
      let value = '—';
      try {
        const v = evalFormula(p.formula || '', { ...scope, age: 30 });
        value = formatDerived(v);
      } catch (_) { /* leave em-dash */ }
      results.push({
        parameter:  p.test,
        result:     value,
        result_alt: '',
        unit:       p.unit || '',
        unit_alt:   '',
        range:      rangeStr || '—',
        flag:       'Normal',
      });
    }

    const today = new Date().toISOString().slice(0, 10);
    return { [today]: { [group.hold_show]: results } };
  };

  // Render the preview HTML for the currently-selected template. Lives in
  // an effect-style callback rather than a useEffect so the iframe updates
  // synchronously when the user toggles the radio.
  const renderPreviewHtml = (group, template) => {
    if (!group) return '';
    const reportData = buildDummyReportData(group);
    const dummyPatient = {
      id: 'PREVIEW-0001',
      fullName: 'Sample Patient',
      age: 30,
      gender: 'Male',
      visitDate: new Date().toISOString().slice(0, 10),
    };
    const dummyUser = { name: 'Preview User', username: 'preview' };
    const interp = {};
    const catRow = categories.find(c => c.cat === group.hold_show);
    if (catRow && catRow.interpretation) interp[group.hold_show] = catRow.interpretation;

    // Preview the same layout the real report will use. Reads lab_format
    // off the first parameter of the lab — every row in a hold_show
    // group carries the same value — so a narrative lab previews as
    // narrative and a parameter lab previews as parameter.
    const catFormats = {};
    const fmt = group.parameters?.[0]?.lab_format === 'narrative' ? 'narrative' : 'parameter';
    catFormats[group.hold_show] = fmt;

    if (template === 'branded') {
      return generateLabReportHTMLDekyi(reportData, dummyPatient, dummyUser, null, {
        verifiedBy:      'Sample Verifier',
        verifiedTitle:   'Senior Medical Laboratory Scientist',
        approvedBy:      'Sample Approver',
        approvedTitle:   'Senior Medical Laboratory Scientist',
        specimenType:    'Blood',
        referringDoctor: 'Dr. Sample',
      }, interp, {}, {}, catFormats);
    }
    return generateLabReportHTML(reportData, dummyPatient, dummyUser, null, interp);
  };

  const handleOpenPreview = (group) => {
    setPreviewGroup(group);
    setPreviewTemplate('branded');
    setPreviewHtml(renderPreviewHtml(group, 'branded'));
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewGroup(null);
    setPreviewHtml('');
  };

  const handleChangePreviewTemplate = (next) => {
    setPreviewTemplate(next);
    setPreviewHtml(renderPreviewHtml(previewGroup, next));
  };

  const handleOpenPreviewInWindow = () => {
    if (!previewHtml) return;
    const w = window.open('', '_blank');
    if (!w) {
      window.alert('Could not open the preview window. Please allow pop-ups for this site and try again.');
      return;
    }
    w.document.write(previewHtml);
    w.document.close();
  };

  const handleSave = async () => {
    // Validate that category is selected
    if (!selectedCategory) {
      await Swal.fire({
        title: 'Category Required',
        text: 'Please select a category before adding parameters',
        icon: 'warning',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Validate that at least one parameter has a name
    const validParameters = formData.filter(param => param.parameterName.trim());
    if (validParameters.length === 0) {
      await Swal.fire({
        title: 'Parameters Required',
        text: 'Please add at least one parameter with a name',
        icon: 'warning',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Validate every dependent parameter has a syntactically-correct formula
    // and doesn't reference itself or an unknown identifier. Dependents may
    // reference other dependents (a calculated value can feed another) — we
    // gate that with findCycles() so a loop (A → B → A) is rejected rather
    // than blowing up the result-entry resolver at runtime.
    const formulaProblems = [];
    const knownNames = new Set(
      validParameters.map(p => p.parameterName.trim().toLowerCase())
    );
    for (const p of validParameters) {
      if (p.type !== 'dependent') continue;
      const expr = (p.formula || '').trim();
      if (!expr) {
        formulaProblems.push(`${p.parameterName.trim()}: formula is required for dependent parameters`);
        continue;
      }
      let ids;
      try { ids = extractIdentifiers(expr); }
      catch (err) {
        formulaProblems.push(`${p.parameterName.trim()}: ${err.message}`);
        continue;
      }
      const selfRef = ids.some(n => n.toLowerCase() === p.parameterName.trim().toLowerCase());
      if (selfRef) {
        formulaProblems.push(`${p.parameterName.trim()}: formula references itself`);
        continue;
      }
      const unknown = ids.filter((n) => {
        const lc = n.toLowerCase();
        return !knownNames.has(lc) && !PATIENT_CONTEXT_IDS.has(lc);
      });
      if (unknown.length > 0) {
        formulaProblems.push(`${p.parameterName.trim()}: unknown parameter${unknown.length === 1 ? '' : 's'} ${unknown.join(', ')}`);
      }
    }
    // Transitive cycle check across the whole lab (e.g. A depends on B,
    // B depends on A). The dfs handles deeper chains too.
    if (formulaProblems.length === 0) {
      const cycles = findCycles(
        validParameters.map(p => ({ name: p.parameterName.trim(), formula: p.formula || '' }))
      );
      for (const cyc of cycles) {
        // findCycles returns lowercased names; render them in the user's
        // original casing so the error message matches what they typed.
        const byLc = new Map(
          validParameters.map(p => [p.parameterName.trim().toLowerCase(), p.parameterName.trim()])
        );
        const pretty = cyc.map(n => byLc.get(n) || n);
        formulaProblems.push(`Formula cycle: ${pretty.join(' → ')}`);
      }
    }
    if (formulaProblems.length > 0) {
      await Swal.fire({
        title: 'Formula problems',
        html: `Fix the following before saving:<br/><br/>` +
              formulaProblems.slice(0, 8).map(m => `• ${m}`).join('<br/>') +
              (formulaProblems.length > 8 ? `<br/>…and ${formulaProblems.length - 8} more` : ''),
        icon: 'warning',
        confirmButtonColor: '#3085d6',
      });
      return;
    }

    try {
      // Show loading
      Swal.fire({
        title: 'Saving Parameters...',
        text: 'Please wait while we save your lab parameters',
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });

      // Split rows: those carrying trans_codes are existing DB groups we
      // wipe-and-rewrite via /updateLabParameterGroup. The rest are new
      // parameters that go through the existing bulk insert.
      const existingRows = validParameters.filter(p => Array.isArray(p.trans_codes) && p.trans_codes.length > 0);
      const newRows      = validParameters.filter(p => !p.trans_codes || p.trans_codes.length === 0);

      // `displayOrder` for each parameter is its position in the visible
      // formData array. We pre-compute the index map against the *full*
      // formData (including blank rows) so that valid-only filtering
      // doesn't shift the order of the saved parameters.
      const orderById = new Map(formData.map((p, i) => [p.id, i]));

      const buildPayload = (param) => ({
        category: selectedCategory,
        parameterName: param.parameterName.trim(),
        // For narrative labs the range / unit / formula fields are
        // meaningless — the user enters paragraph text per section.
        // We send them empty so a lab that was previously a parameter
        // lab and got switched to narrative doesn't keep stale numeric
        // metadata in the DB.
        type:          labFormat === 'narrative' ? 'independent' : param.type,
        maleRange:     labFormat === 'narrative' ? '' : param.maleRange,
        femaleRange:   labFormat === 'narrative' ? '' : param.femaleRange,
        childrenRange: labFormat === 'narrative' ? '' : param.childrenRange,
        neonateRange:  labFormat === 'narrative' ? '' : param.neonateRange,
        unit:          labFormat === 'narrative' ? '' : param.unit,
        altUnit:       labFormat === 'narrative' ? '' : (param.altUnit || ''),
        conversionFactor: labFormat === 'narrative'
          ? null
          : (param.conversionFactor === '' || param.conversionFactor === null
              ? null
              : Number(param.conversionFactor)),
        // Derived: only flag as such when type is explicitly dependent AND a
        // formula has been entered. Backend re-validates the same way.
        isDerived: labFormat === 'narrative'
          ? false
          : (param.type === 'dependent' && (param.formula || '').trim().length > 0),
        formula: labFormat === 'narrative'
          ? ''
          : (param.type === 'dependent' ? (param.formula || '').trim() : ''),
        flags:        labFormat === 'narrative' ? '' : param.flags,
        displayOrder: orderById.get(param.id) ?? 0,
        labFormat,                // 'parameter' | 'narrative' — migration 037
      });

      let updatedCount = 0;
      let createdCount = 0;
      const failures = [];

      // Update existing parameter groups one-by-one. Each row in the
      // dialog corresponds to one logical parameter (possibly multiple
      // flag-rows in the DB) — the endpoint wipes and rewrites all of
      // them in a single call.
      for (const param of existingRows) {
        try {
          const resp = await axios.post(updateLabParameterGroup, {
            transCodes: param.trans_codes,
            ...buildPayload(param),
          });
          if (resp.data && resp.data.success) updatedCount++;
          else failures.push({ name: param.parameterName, msg: resp.data?.message || 'Update failed' });
        } catch (err) {
          failures.push({ name: param.parameterName, msg: err.response?.data?.message || err.message });
        }
      }

      // Brand-new parameters use the existing bulk insert endpoint.
      let response = { data: { success: true } };
      if (newRows.length > 0) {
        response = await axios.post(addLabParameters, {
          parameters: newRows.map(buildPayload),
        });
        if (response.data?.success) {
          createdCount = newRows.length;
        }
      }

      // Drop rows the user removed from the editor. Without this step the
      // remove button on a loaded row would be a silent no-op for the
      // backend and the parameter would reappear after refresh. Each
      // trans_code is one flag-row in the haematology table, so a logical
      // parameter with N flags contributes N trans_codes here.
      let deletedCount = 0;
      const uniqueRemoved = Array.from(new Set(removedTransCodes)).filter(Boolean);
      for (const tc of uniqueRemoved) {
        try {
          await axios.delete(deleteLabParameter(tc));
          deletedCount++;
        } catch (err) {
          failures.push({ name: `trans_code ${tc}`, msg: err.response?.data?.message || err.message });
        }
      }

      // Close loading
      Swal.close();

      const overallSuccess = response.data.success && failures.length === 0;
      if (overallSuccess && (updatedCount > 0 || createdCount > 0 || deletedCount > 0)) {
        const parts = [];
        if (updatedCount > 0) parts.push(`${updatedCount} updated`);
        if (createdCount > 0) parts.push(`${createdCount} created`);
        if (deletedCount > 0) parts.push(`${deletedCount} row${deletedCount === 1 ? '' : 's'} removed`);
        response.data.message = `Successfully saved (${parts.join(', ')}).`;
      }

      if (overallSuccess) {
        // Show success message
        await Swal.fire({
          title: 'Success!',
          text: response.data.message,
          icon: 'success',
          confirmButtonColor: '#11998e',
          confirmButtonText: 'Great!',
          timer: 2000,
          timerProgressBar: true
        });

        // Reset form BUT keep dialog open. Drop the loaded-lab marker so
        // we're back in "create new" mode by default; the user can still
        // re-pick a lab to keep editing the same one.
        setFormData([{
          id: Date.now(),
          trans_codes: [],
          parameterName: '',
          type: '',
          maleRange: '',
          femaleRange: '',
          childrenRange: '',
          neonateRange: '',
          unit: '',
          altUnit: '',
          conversionFactor: '',
          flags: ''
        }]);
        setLoadedLabName('');
        setRemovedTransCodes([]); // orphans cleared, fresh session
        // Keep selectedCategory so user can add more parameters to the same category

        // Refresh the parameters list in background
        fetchHaematologyParameters();
      } else {
        // Show error message — include any per-row failures gathered above.
        const failLines = failures.slice(0, 5).map(f => `• ${f.name}: ${f.msg}`).join('\n');
        const extra = failures.length > 5 ? `\n…and ${failures.length - 5} more` : '';
        await Swal.fire({
          title: failures.length > 0 ? `Saved ${updatedCount + createdCount}, failed ${failures.length}` : 'Error',
          text: failures.length > 0
            ? `Some parameters could not be saved:\n${failLines}${extra}`
            : (response.data.message || 'Failed to save parameters'),
          icon: 'error',
          confirmButtonColor: '#d33',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error saving parameters:', error);
      Swal.fire({
        title: 'Error',
        text: 'Error saving parameters: ' + error.message,
        icon: 'error',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleOpenCategoryDialog = () => {
    setOpenCategoryDialog(true);
  };

  const handleCloseCategoryDialog = () => {
  setOpenCategoryDialog(false);
  setCategoryFormData({
    categoryName: '',
    waitingTime: '',
    interpretation: ''
  });
};

  const handleCategoryInputChange = (field, value) => {
    setCategoryFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveCategory = async () => {
    // Check if category already exists
    const existingCategory = categories.find(cat => 
      cat.cat.toLowerCase() === categoryFormData.categoryName.trim().toLowerCase()
    );

    if (categoryFormData.waitingTime && (isNaN(categoryFormData.waitingTime) || categoryFormData.waitingTime < 0)) {
    await Swal.fire({
      title: 'Invalid Waiting Time',
      text: 'Waiting time must be a positive number',
      icon: 'warning',
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'OK'
    });
    return;
  }

    try {
      const response = await axios.post(addLabCategory, {
      category: categoryFormData.categoryName,
      waitingTime: categoryFormData.waitingTime || 0,
      interpretation: categoryFormData.interpretation || null
    });
      
      if (response.data.success) {
        await Swal.fire({
          title: 'Success!',
          text: 'Category saved successfully',
          icon: 'success',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Great!'
        });
        handleCloseCategoryDialog();
        fetchCategories(); // Refresh categories list
      }
    } catch (error) {
      console.error('Error saving category:', error);
      await Swal.fire({
        title: 'Error',
        text: 'Error saving category: ' + (error.response?.data?.message || error.message),
        icon: 'error',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(loadLabCategories);
      if (response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleEditCategory = (category) => {
    
    setEditingCategory(category);
   setEditCategoryFormData({
  categoryName: category.cat,
  waitingTime: category.waiting_time || '',
  interpretation: category.interpretation || ''
});
  };

  const handleCloseEditDialog = () => {
    setEditingCategory(null);
   setEditCategoryFormData({
  categoryName: '',
  waitingTime:  '',
  interpretation: ''
});
  };

  const handleDeleteCategory = async (categoryId) => {
    // Resolve the category by trans_code so the prompt can name it
    // (much clearer than "this category"). Categories live in `categories`.
    const targetCategory = (categories || []).find(c => c.trans_code === categoryId);
    const categoryName = targetCategory?.cat || 'this category';

    // Branded confirmation — explicit about what gets wiped vs preserved.
    // The cascading delete on the backend removes parameters AND every
    // insurance price record linked to this lab; patient results and
    // historical billing are intentionally untouched.
    const result = await Swal.fire({
      title: `Delete "${categoryName}"?`,
      html: `
        <div style="text-align:left; font-size: 0.95rem; line-height: 1.5;">
          This will permanently remove:
          <ul style="margin: 8px 0 0 20px; padding: 0;">
            <li>All <b>parameters</b> defined under this lab</li>
            <li>All <b>insurance price records</b> for this lab</li>
            <li>The lab category itself</li>
          </ul>
          <div style="margin-top:10px; padding: 8px 10px; background: #ecfdf5; border-left: 3px solid #10b981; border-radius: 4px; font-size: 0.85rem;">
            <b>Patient results and past billing are NOT affected.</b>
          </div>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#9ca3af',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(deleteLabCategory(categoryId));
        if (response.data.success) {
          await Swal.fire({
            title: 'Deleted',
            // Use the backend's count-summary message when present
            // ("3 parameters and 5 price records removed…").
            text: response.data.message || 'Category deleted successfully.',
            icon: 'success',
            confirmButtonColor: '#11998e',
            confirmButtonText: 'OK',
            timer: 2400,
            timerProgressBar: true,
          });
          fetchCategories(); // Refresh categories list
        } else {
          await Swal.fire({
            title: 'Could not delete',
            text: response.data.message || 'Failed to delete category.',
            icon: 'error',
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'OK'
          });
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        await Swal.fire({
          title: 'Error',
          text: 'Error deleting category: ' + (error.response?.data?.message || error.message),
          icon: 'error',
          confirmButtonColor: '#dc2626',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  const handleEditParameter = (parameter) => {
    setEditingParameter(parameter);
    setEditParameterFormData({
      parameterName: parameter.test,
      type: parameter.is_derived ? 'dependent' : (parameter.type || 'independent'),
      maleRange: parameter.male || '',
      femaleRange: parameter.female || '',
      childrenRange: parameter.children || '',
      neonateRange: parameter.neonates || '',
      unit: parameter.unit || '',
      altUnit: parameter.alt_unit || '',
      conversionFactor: parameter.unit_factor === null || parameter.unit_factor === undefined
        ? ''
        : String(parameter.unit_factor),
      isDerived: !!parameter.is_derived,
      formula: parameter.formula || '',
      flags: parameter.flag || ''
    });
  };

  const handleCloseEditParameterDialog = () => {
    setEditingParameter(null);
    setEditParameterFormData({
      parameterName: '',
      type: 'independent',
      maleRange: '',
      femaleRange: '',
      childrenRange: '',
      neonateRange: '',
      unit: '',
      altUnit: '',
      conversionFactor: '',
      isDerived: false,
      formula: '',
      flags: ''
    });
  };

 const handleUpdateCategory = async () => {
  try {
    const response = await axios.put(updateLabCategory(editingCategory.trans_code), {
      newCategoryName: editCategoryFormData.categoryName,
      waitingTime: editCategoryFormData.waitingTime || 0,
      interpretation: editCategoryFormData.interpretation || null
    });
    
    if (response.data.success) {
      await Swal.fire({
        title: 'Success!',
        text: 'Category updated successfully. All related lab records have been updated.',
        icon: 'success',
        confirmButtonColor: '#11998e',
        confirmButtonText: 'Great!'
      });
      handleCloseEditDialog();
      fetchCategories(); // Refresh categories list
      fetchHaematologyParameters(); // Refresh lab parameters to show updated categories
    }
  } catch (error) {
    console.error('Error updating category:', error);
    await Swal.fire({
      title: 'Error',
      text: 'Error updating category: ' + (error.response?.data?.message || error.message),
      icon: 'error',
      confirmButtonColor: '#d33',
      confirmButtonText: 'OK'
    });
  }
};

  const handleOpenInterpDialog = (category) => {
    setInterpDialogCategory(category);
    const html = category.interpretation || '';
    setInterpDialogValue(html);
    setInterpTableData(parseInterpHTML(html));
    setInterpEditMode('visual');
    setInterpAiError('');
  };

  const handleCloseInterpDialog = () => {
    setInterpDialogCategory(null);
    setInterpDialogValue('');
    setInterpTableData({ comment: '', headers: [], rows: [] });
    setInterpEditMode('visual');
    setInterpAiError('');
  };

  const handleSwitchInterpMode = (newMode) => {
    // Sync data between modes before switching
    if (interpEditMode === 'visual') {
      const built = buildInterpHTML(interpTableData);
      setInterpDialogValue(built);
    } else if (newMode === 'visual') {
      setInterpTableData(parseInterpHTML(interpDialogValue));
    }
    setInterpEditMode(newMode);
  };

  const handleGenerateInterpWithAi = async (labName) => {
    const name = String(labName || interpDialogCategory?.cat || '').trim();
    if (!name) return;
    setInterpAiGenerating(true);
    setInterpAiError('');
    try {
      const { data } = await axios.post(generateLabInterpretation, { labName: name });
      if (!data?.success) throw new Error(data?.message || 'AI returned no content.');
      const html = data.html || '';
      setInterpDialogValue(html);
      setInterpTableData(parseInterpHTML(html));
      setInterpEditMode('visual');
    } catch (err) {
      setInterpAiError(err.response?.data?.message || err.message || 'AI generation failed.');
    } finally {
      setInterpAiGenerating(false);
    }
  };

  const handleSaveInterp = async () => {
    if (!interpDialogCategory) return;
    // If in visual mode, build HTML from table data first
    const finalHtml = interpEditMode === 'visual'
      ? buildInterpHTML(interpTableData)
      : interpDialogValue;
    setInterpDialogSaving(true);
    try {
      const response = await axios.put(updateLabCategory(interpDialogCategory.trans_code), {
        newCategoryName: interpDialogCategory.cat,
        waitingTime: interpDialogCategory.waiting_time || 0,
        interpretation: finalHtml.trim() || null,
      });
      if (response.data.success) {
        await Swal.fire({
          title: 'Saved!',
          text: `Interpretation table saved for "${interpDialogCategory.cat}".`,
          icon: 'success',
          confirmButtonColor: '#11998e',
          timer: 1800,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        fetchCategories();
        handleCloseInterpDialog();
      }
    } catch (error) {
      const serverDetail = error.response?.data?.error;
      const baseMsg      = error.response?.data?.message || error.message;
      await Swal.fire({
        title: 'Error',
        text: serverDetail ? `${baseMsg} — ${serverDetail}` : baseMsg,
        icon: 'error',
        confirmButtonColor: '#d33',
      });
    } finally {
      setInterpDialogSaving(false);
    }
  };

  const handleUpdateParameter = async () => {
    if (!editingParameter) return;

    try {
      // Show loading
      Swal.fire({
        title: 'Updating Parameter...',
        text: 'Please wait while we update the lab parameter',
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await axios.put(updateLabParameter(editingParameter.trans_code), {
        parameterName: editParameterFormData.parameterName.trim(),
        maleRange: editParameterFormData.maleRange || null,
        femaleRange: editParameterFormData.femaleRange || null,
        childrenRange: editParameterFormData.childrenRange || null,
        neonateRange: editParameterFormData.neonateRange || null,
        unit: editParameterFormData.unit || null,
        altUnit: editParameterFormData.altUnit || null,
        conversionFactor: editParameterFormData.conversionFactor === '' || editParameterFormData.conversionFactor === null
          ? null
          : Number(editParameterFormData.conversionFactor),
        isDerived: editParameterFormData.type === 'dependent' && (editParameterFormData.formula || '').trim().length > 0,
        formula: editParameterFormData.type === 'dependent' ? (editParameterFormData.formula || '').trim() : '',
        flags: editParameterFormData.flags || null
      });

      // Close loading and show success
      Swal.fire({
        title: 'Success!',
        text: 'Parameter updated successfully',
        icon: 'success',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Great!'
      });

      handleCloseEditParameterDialog();
      fetchHaematologyParameters(); // Refresh the parameters list

    } catch (error) {
      console.error('Error updating parameter:', error);
      Swal.fire({
        title: 'Error',
        text: 'Error updating parameter: ' + (error.response?.data?.message || error.message),
        icon: 'error',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleDeleteParameter = async (parameter) => {
    // A logical parameter can occupy multiple DB rows — one per allowed flag
    // (the dedupe pass on render collapses them). We delete every underlying
    // trans_code, not just the one the deduped row happens to carry, or the
    // parameter would seem to "come back" after refresh because the other
    // flag-rows still exist in the haematology table.
    const transCodes = Array.isArray(parameter._originals) && parameter._originals.length > 0
      ? parameter._originals.map(o => o.trans_code).filter(Boolean)
      : (parameter.trans_code ? [parameter.trans_code] : []);

    if (transCodes.length === 0) {
      await Swal.fire({
        title: 'Cannot delete',
        text: 'This parameter has no trans_code on record — nothing to delete.',
        icon: 'warning',
        confirmButtonColor: '#d33',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Are you sure you want to delete the parameter "${parameter.test}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: 'Deleting Parameter...',
        text: 'Please wait while we delete the lab parameter',
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => { Swal.showLoading(); }
      });

      let deleted = 0;
      const failures = [];
      for (const tc of transCodes) {
        try {
          await axios.delete(deleteLabParameter(tc));
          deleted++;
        } catch (err) {
          failures.push(err.response?.data?.message || err.message);
        }
      }

      Swal.close();

      if (failures.length === 0) {
        await Swal.fire({
          title: 'Deleted!',
          text: `Parameter deleted successfully (${deleted} flag row${deleted === 1 ? '' : 's'}).`,
          icon: 'success',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Great!',
          timer: 1800,
        });
      } else {
        await Swal.fire({
          title: 'Partially deleted',
          html: `Removed ${deleted} of ${transCodes.length} flag rows. ` +
                `${failures.length} failed:<br/>${failures.slice(0, 3).map(m => `• ${m}`).join('<br/>')}`,
          icon: 'warning',
          confirmButtonColor: '#d33',
        });
      }

      fetchHaematologyParameters();
    } catch (error) {
      console.error('Error deleting parameter:', error);
      Swal.fire({
        title: 'Error',
        text: 'Error deleting parameter: ' + (error.response?.data?.message || error.message),
        icon: 'error',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    }
  };

  // Filtered data for display
  const filteredCategories = categories.filter(category =>
    category.cat.toLowerCase().includes(categorySearchTerm.toLowerCase())
  );

  const filteredLabData = haematologyData.filter(group => {
    const matchesSearch = group.hold_show.toLowerCase().includes(labSearchTerm.toLowerCase()) ||
                         group.parameters.some(param => 
                           param.test.toLowerCase().includes(labSearchTerm.toLowerCase())
                         );
    const matchesCategory = selectedCategoryFilter === 'all' || 
                           group.parameters.some(param => param.cat === selectedCategoryFilter);
    return matchesSearch && matchesCategory;
  });

  return (
    <Box sx={{
      p: { xs: 2, md: 3 },
      // Fill whatever vertical room is left after the app bar so the two
      // cards can stretch all the way to the bottom of the viewport.
      minHeight: 'calc(100vh - 64px)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Two-column layout that fills the rest of the viewport. Cards inside
          each column flex to fill that height, and the inner TableContainer
          scrolls so the action bar + pagination stay pinned. */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        gap: 2.5,
        minHeight: 0,
      }}>
        {/* Categories Section - Left Side */}
        <Box sx={{
          width: { xs: '100%', lg: '33.333%' },
          display: 'flex', flexDirection: 'column', minHeight: 0,
        }}>
          <Card sx={{
            border: '1px solid', borderColor: 'divider', boxShadow: 'none',
            flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
          }}>
            <CardContent sx={{
              p: 2, '&:last-child': { pb: 2 },
              display: 'flex', flexDirection: 'column',
              flex: 1, minHeight: 0,
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
                    Lab Categories
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filteredCategories.length} categor{filteredCategories.length === 1 ? 'y' : 'ies'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CategoryIcon sx={{ fontSize: 16 }} />}
                  onClick={handleOpenCategoryDialog}
                  sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, py: 0.5, px: 1.5, fontSize: '0.8rem' }}
                >
                  Add Category
                </Button>
              </Box>

              {/* Categories Search */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search categories…"
                value={categorySearchTerm}
                onChange={(e) => setCategorySearchTerm(e.target.value)}
                sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
                InputProps={{
                  startAdornment: <ScienceIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 18 }} />,
                }}
              />

              {/* Bordered wrapper splits scrollable table area from pinned
                  pagination. flex: 1 + minHeight: 0 lets the table flex
                  inside the card without overflowing the viewport. */}
              <Box sx={{
                border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                flex: 1, minHeight: 0,
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}>
              <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <Table size="small" stickyHeader sx={{
                  '& .MuiTableCell-root': { py: 0.75, fontSize: '0.82rem' },
                  '& .MuiTableCell-head': { py: 1, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '.4px', color: 'text.secondary', bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' },
                }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Category Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 140, textAlign: 'right' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCategories
                      .slice(categoryPage * categoryRowsPerPage, categoryPage * categoryRowsPerPage + categoryRowsPerPage)
                      .map((category) => (
                        <TableRow key={category.id} hover>
                          <TableCell sx={{ fontWeight: 500, color: 'text.primary', textTransform: 'uppercase' }}>
                            {category.cat}
                            {category.interpretation && (
                              <Chip
                                label="Table set"
                                size="small"
                                sx={{ ml: 1, fontSize: '0.65rem', height: 18, bgcolor: '#e8f5e9', color: '#2e7d32' }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <Tooltip title="Set interpretation / reference table">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenInterpDialog(category)}
                                sx={{ p: 0.5, color: category.interpretation ? '#11998e' : '#9e9e9e' }}
                              >
                                <TableChartIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditCategory(category)}
                              sx={{ p: 0.5, ml: 0.25 }}
                            >
                              <EditIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteCategory(category.trans_code)}
                              sx={{ p: 0.5, ml: 0.25 }}
                            >
                              <DeleteIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    {filteredCategories.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                          {categorySearchTerm ? 'No categories match your search' : 'No categories found'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 15, 25, 50]}
                component="div"
                count={filteredCategories.length}
                rowsPerPage={categoryRowsPerPage}
                page={categoryPage}
                onPageChange={handleChangeCategoryPage}
                onRowsPerPageChange={handleChangeCategoryRowsPerPage}
                sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0, '& .MuiTablePagination-toolbar': { minHeight: 40 } }}
              />
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Lab Parameters Section - Right Side */}
        <Box sx={{
          width: { xs: '100%', lg: '66.667%' },
          display: 'flex', flexDirection: 'column', minHeight: 0,
        }}>
          <Card sx={{
            border: '1px solid', borderColor: 'divider', boxShadow: 'none',
            flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
          }}>
            <CardContent sx={{
              p: 2, '&:last-child': { pb: 2 },
              display: 'flex', flexDirection: 'column',
              flex: 1, minHeight: 0,
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
                    Lab Parameters
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filteredLabData.length} test group{filteredLabData.length === 1 ? '' : 's'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={handleOpenDialog}
                  sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, py: 0.5, px: 1.5, fontSize: '0.8rem' }}
                >
                  Add Parameter
                </Button>
              </Box>

              {/* Lab Parameters Filters */}
              <Box sx={{ display: 'flex', gap: 1.25, mb: 1.5 }}>
                <TextField
                  size="small"
                  placeholder="Search parameters…"
                  value={labSearchTerm}
                  onChange={(e) => setLabSearchTerm(e.target.value)}
                  sx={{ flex: 1, '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
                  InputProps={{
                    startAdornment: <ScienceIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 18 }} />,
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Filter by Category</InputLabel>
                  <Select
                    value={selectedCategoryFilter}
                    label="Filter by Category"
                    onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                    sx={{ fontSize: '0.85rem' }}
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.cat}>
                        {category.cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 300 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{
                  border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                  flex: 1, minHeight: 0,
                  display: 'flex', flexDirection: 'column',
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                }}>
                <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  <Table size="small" stickyHeader sx={{
                    '& .MuiTableCell-root': { py: 0.75, fontSize: '0.82rem' },
                    '& .MuiTableCell-head': { py: 1, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '.4px', color: 'text.secondary', bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' },
                  }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: 44 }}></TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Test Group</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 130 }}>Parameters</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 110, textAlign: 'right' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLabData
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((group, index) => {
                          const actualIndex = page * rowsPerPage + index;
                          const isExpanded = expandedRows.has(actualIndex);
                          const dedupedParams = dedupeParameters(group.parameters);
                          return (
                            <React.Fragment key={actualIndex}>
                              <TableRow hover>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleRowExpansion(actualIndex)}
                                    sx={{ p: 0.25 }}
                                  >
                                    {isExpanded ? <KeyboardArrowUpIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: 'text.primary', textTransform: 'uppercase' }}>
                                  {group.hold_show}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={`${dedupedParams.length} param${dedupedParams.length === 1 ? '' : 's'}`}
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: 'rgba(84,104,255,0.14)', color: '#5468FF' }}
                                  />
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                  <Tooltip title="Preview report — see how this lab will look with sample values" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); handleOpenPreview(group); }}
                                      sx={{ p: 0.5, color: '#5468FF' }}
                                    >
                                      <VisibilityIcon sx={{ fontSize: 17 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Set / generate interpretation table for this lab" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const catObj = categories.find(c => c.cat === group.hold_show)
                                          || { cat: group.hold_show, trans_code: null, waiting_time: 0, interpretation: '' };
                                        handleOpenInterpDialog(catObj);
                                      }}
                                      sx={{ p: 0.5, ml: 0.25, color: categories.find(c => c.cat === group.hold_show)?.interpretation ? '#11998e' : '#9e9e9e' }}
                                    >
                                      <TableChartIcon sx={{ fontSize: 17 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Edit this lab — loads all parameters into the editor" arrow>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={(e) => { e.stopPropagation(); handleEditLabGroup(group.hold_show); }}
                                      sx={{ p: 0.5, ml: 0.25 }}
                                    >
                                      <EditIcon sx={{ fontSize: 17 }} />
                                    </IconButton>
                                  </Tooltip>
                                  {/* Info — opens the read-only pricing dialog
                                      for this lab + a "Request change" form
                                      that admins review. Hidden entirely when
                                      the facility toggle is OFF. */}
                                  {labPriceInfoEnabled && (
                                    <Tooltip title="View pricing details & request changes" arrow>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); openLabInfo(group.hold_show); }}
                                        sx={{ p: 0.5, ml: 0.25, color: '#0ea5e9' }}
                                      >
                                        <InfoIcon sx={{ fontSize: 17 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Delete the entire lab and all its parameters" arrow>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => { e.stopPropagation(); handleDeleteLabGroup(group); }}
                                      sx={{ p: 0.5, ml: 0.25 }}
                                    >
                                      <DeleteIcon sx={{ fontSize: 17 }} />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4} sx={{ bgcolor: 'background.default' }}>
                                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ m: 1, p: 1.25, borderRadius: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
                                        Parameters in {group.hold_show}
                                      </Typography>
                                      <Table size="small" sx={{
                                        '& .MuiTableCell-root': { py: 0.6, fontSize: '0.78rem' },
                                        '& .MuiTableCell-head': { py: 0.85, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '.4px', color: 'text.secondary', bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' },
                                      }}>
                                        <TableHead>
                                          <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Test Parameter</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Male</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Female</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Children</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Neonates</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Flags</TableCell>
                                            <TableCell sx={{ fontWeight: 700, width: 96, textAlign: 'right' }}>Actions</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {dedupedParams.map((param) => (
                                            <TableRow key={param.trans_code} hover>
                                              <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>{param.test}</TableCell>
                                              <TableCell>{param.male || '—'}</TableCell>
                                              <TableCell>{param.female || '—'}</TableCell>
                                              <TableCell>{param.children || '—'}</TableCell>
                                              <TableCell>{param.neonates || '—'}</TableCell>
                                              <TableCell>
                                                {param.unit || '—'}
                                                {param.alt_unit && param.unit_factor ? (
                                                  <Tooltip
                                                    arrow
                                                    title={`1 ${param.unit} = ${param.unit_factor} ${param.alt_unit}`}
                                                  >
                                                    <Chip
                                                      label={`↔ ${param.alt_unit}`}
                                                      size="small"
                                                      sx={{
                                                        ml: 0.75, height: 18, fontSize: '0.62rem', fontWeight: 700,
                                                        bgcolor: 'rgba(17,153,142,0.18)',
                                                        color: '#4ade80',
                                                        border: '1px solid rgba(74,222,128,0.30)',
                                                        '& .MuiChip-label': { px: 0.75 },
                                                      }}
                                                    />
                                                  </Tooltip>
                                                ) : null}
                                              </TableCell>
                                              <TableCell>
                                                {param.flag ? (
                                                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                    {param.flag.split(',').map((flag, idx) => (
                                                      <Chip
                                                        key={idx}
                                                        label={flag.trim()}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.66rem', height: 18 }}
                                                      />
                                                    ))}
                                                  </Stack>
                                                ) : '—'}
                                              </TableCell>
                                              <TableCell sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <IconButton
                                                  size="small"
                                                  color="primary"
                                                  onClick={() => handleEditParameter(param)}
                                                  sx={{ p: 0.5 }}
                                                >
                                                  <EditIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                                <IconButton
                                                  size="small"
                                                  color="error"
                                                  onClick={() => handleDeleteParameter(param)}
                                                  sx={{ p: 0.5, ml: 0.25 }}
                                                >
                                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 15, 25, 50, 100]}
                  component="div"
                  count={filteredLabData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0, '& .MuiTablePagination-toolbar': { minHeight: 40 } }}
                />
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Add Lab Parameter Dialog */}
     <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            maxHeight: '94vh',
            // Push the dialog out wider than MUI's xl breakpoint so the
            // 10-column parameter grid (Name | Type | Ranges×4 | Unit | Alt
            // Unit | Factor | Flags) can breathe without squishing each
            // field below readability.
            width: { md: '95vw', lg: '92vw', xl: '88vw' },
            bgcolor: 'background.paper',
          }
        }}
      >
        {/* Gradient header bar */}
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              px: 3, py: 2.25,
              color: 'white',
              display: 'flex', alignItems: 'center', gap: 2,
            }}
          >
            <Box sx={{
              width: 44, height: 44, borderRadius: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.28)',
              backdropFilter: 'blur(10px)',
            }}>
              <ScienceIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{
                fontSize: '0.68rem', fontWeight: 700, letterSpacing: '1.4px',
                textTransform: 'uppercase', opacity: 0.85, lineHeight: 1,
              }}>
                Lab Configuration
              </Typography>
              <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, mt: 0.4, lineHeight: 1.2 }}>
                Add Lab Parameters
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', opacity: 0.9, mt: 0.25 }}>
                Define parameters for laboratory tests
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseDialog}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.12)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: 'action.hover', p: { xs: 2, md: 3 }, position: 'relative' }}>
          {/* Loading overlay — shown while a lab's parameters are being
              loaded into the form. Sits above the content but inside the
              DialogContent so the close button + footer stay clickable
              (escape hatch in case the load somehow stalls). */}
          {loadingLabGroup && (
            <Box sx={{
              position: 'absolute', inset: 0,
              zIndex: 10,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 1.5,
              bgcolor: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(0,0,0,0.65)'
                : 'rgba(255,255,255,0.78)',
              backdropFilter: 'blur(2px)',
              borderRadius: 1,
            }}>
              <CircularProgress
                size={44}
                thickness={4}
                sx={{ color: '#38ef7d' }}
              />
              <Box sx={{
                fontSize: '0.95rem', fontWeight: 700,
                color: 'text.primary', letterSpacing: '-0.01em',
              }}>
                Loading parameters…
              </Box>
              <Box sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                {loadedLabName ? `Preparing "${String(loadedLabName).toUpperCase()}"` : 'Please wait'}
              </Box>
            </Box>
          )}
          <Stack spacing={2.5}>
            {/* Step 1 — Category */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid', borderColor: 'divider',
                bgcolor: 'background.paper',
                overflow: 'hidden',
              }}
            >
              <Box sx={{
                px: 2.5, py: 1.5,
                borderBottom: '1px solid', borderColor: 'divider',
                display: 'flex', alignItems: 'center', gap: 1.25,
              }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: 1.25,
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  color: 'white', fontWeight: 800, fontSize: '0.85rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 3px 8px rgba(17,153,142,0.32)',
                }}>
                  1
                </Box>
                <Typography sx={{
                  fontSize: '0.95rem', fontWeight: 700, color: 'text.primary',
                  flexGrow: 1,
                }}>
                  Select Category &middot; or load an existing lab to edit
                </Typography>
                {selectedCategory && (
                  <CheckCircleIcon sx={{ fontSize: 20, color: '#38ef7d' }} />
                )}
              </Box>

              <Box sx={{ p: 2.5 }}>
                {/* ── Load an existing lab ─────────────────────────────
                    Bumped to the very top of Step 1 so it's the first
                    thing the user sees on opening the dialog. Autocomplete
                    lets you type-to-search even when 50+ labs exist. */}
                <Box sx={{
                  p: 1.75, mb: 2,
                  borderRadius: 1.5,
                  border: '1.5px dashed',
                  borderColor: loadedLabName ? 'rgba(56,239,125,0.50)' : 'rgba(17,153,142,0.40)',
                  bgcolor: loadedLabName ? 'rgba(56,239,125,0.06)' : 'rgba(17,153,142,0.04)',
                }}>
                  <Box sx={{
                    fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    color: loadedLabName ? '#4ade80' : '#11998e',
                    mb: 1,
                    display: 'flex', alignItems: 'center', gap: 0.75,
                  }}>
                    <ScienceIcon sx={{ fontSize: 14 }} />
                    {loadedLabName ? 'Editing existing lab' : 'Edit an existing lab (optional)'}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      options={haematologyData.map(g => g.hold_show).filter(Boolean)}
                      value={loadedLabName || null}
                      onChange={(_e, value) => {
                        if (value) loadLabIntoForm(value);
                      }}
                      isOptionEqualToValue={(opt, val) => opt === val}
                      getOptionLabel={(opt) => {
                        if (!opt) return '';
                        const group = haematologyData.find(g => g.hold_show === opt);
                        const count = group ? dedupeParameters(group.parameters).length : 0;
                        return `${String(opt).toUpperCase()} (${count} param${count === 1 ? '' : 's'})`;
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Pick a lab to load its parameters"
                          placeholder="Type to search — e.g. Liver Function Test"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <ScienceIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                      sx={{ flex: 1 }}
                    />
                    {loadedLabName && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={resetEditingLab}
                        sx={{
                          textTransform: 'none', fontWeight: 700,
                          borderRadius: 1.5, py: 0.6,
                          borderColor: 'rgba(56,239,125,0.40)',
                          color: '#4ade80',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            borderColor: '#4ade80',
                            bgcolor: 'rgba(56,239,125,0.08)',
                          },
                        }}
                      >
                        Clear &amp; start new
                      </Button>
                    )}
                  </Box>
                </Box>

                {/* ── Category ───────────────────────────────────────── */}
                <FormControl fullWidth size="small">
                  <InputLabel>Category *</InputLabel>
                  <Select
                    value={selectedCategory}
                    label="Category *"
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.cat}>
                        {category.cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* ── Result Format ──────────────────────────────────────
                    Two shapes of lab supported:
                    • Parameter — numeric/qualitative with reference ranges
                      and units (the default; every existing lab).
                    • Narrative — paragraph-per-section with a single
                      clinical Impression. Used for tests like Peripheral
                      Blood Smear where the result is a description, not
                      a measurement. Switching the toggle changes which
                      sub-fields render on each parameter row below. */}
                <Box sx={{
                  mt: 1.5, p: 1.5, borderRadius: 1.5,
                  border: '1.5px solid',
                  borderColor: labFormat === 'narrative' ? 'rgba(180,83,9,0.40)' : 'divider',
                  background: labFormat === 'narrative'
                    ? 'linear-gradient(135deg, rgba(180,83,9,0.04) 0%, rgba(217,119,6,0.04) 100%)'
                    : 'transparent',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.primary', letterSpacing: 0.4, textTransform: 'uppercase', mb: 0.5 }}>
                        Result format
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.4 }}>
                        {labFormat === 'narrative'
                          ? 'Each row is a section title; the tech enters paragraph text per section plus a clinical Impression at the bottom. The report renders with descriptive sections instead of a value table.'
                          : 'Each row is a parameter with reference ranges, units, and optional flags. The default for most labs.'}
                      </Typography>
                    </Box>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <Select
                        value={labFormat}
                        onChange={(e) => setLabFormat(e.target.value)}
                        sx={{ fontWeight: 700 }}
                      >
                        <MenuItem value="parameter">Parameter (numeric)</MenuItem>
                        <MenuItem value="narrative">Narrative (descriptive)</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {/* ── AI Assist ──────────────────────────────────────────
                    Compact entry point sitting right below the category.
                    For when the lab the user wants doesn't exist yet — they
                    type the name, the AI fills in every parameter, range
                    and unit, and the user just reviews and saves. */}
                <Box sx={{
                  mt: 1.5, p: 1.5,
                  borderRadius: 1.5,
                  border: '1.5px dashed rgba(124, 58, 237, 0.40)',
                  background: 'linear-gradient(135deg, rgba(124,58,237,0.06) 0%, rgba(236,72,153,0.06) 100%)',
                  display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap',
                }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 1.25,
                    background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
                    color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 3px 10px rgba(124,58,237,0.32)',
                    flexShrink: 0,
                  }}>
                    <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.25 }}>
                      Don&apos;t see the lab you need?
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.3 }}>
                      Let AI draft the full parameter set for you &mdash; ranges, units, derived calcs.
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                    onClick={handleOpenAiDialog}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
                      boxShadow: '0 3px 10px rgba(124,58,237,0.28)',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #6d28d9 0%, #db2777 100%)',
                        boxShadow: '0 5px 14px rgba(124,58,237,0.4)',
                      },
                    }}
                  >
                    Generate with AI
                  </Button>
                </Box>

                {/* Mode banner — shows when a lab is loaded so the user
                    is reminded their save will overwrite the existing rows. */}
                {(() => {
                  const existingCount = formData.filter(p => p.trans_codes && p.trans_codes.length > 0).length;
                  const newCount = formData.length - existingCount;
                  if (existingCount === 0) return null;
                  return (
                    <Box sx={{
                      mt: 1.5, p: 1.25, borderRadius: 1.5,
                      bgcolor: 'rgba(56,239,125,0.10)',
                      border: '1px solid rgba(56,239,125,0.30)',
                      display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap',
                    }}>
                      <Box sx={{
                        fontSize: '0.78rem', color: 'text.primary',
                      }}>
                        <strong>{existingCount}</strong> existing parameter{existingCount === 1 ? '' : 's'} loaded
                        {newCount > 0 && <> &middot; <strong>{newCount}</strong> new row{newCount === 1 ? '' : 's'} added</>}
                      </Box>
                      <Box sx={{ ml: 'auto', fontSize: '0.74rem', color: 'text.secondary' }}>
                        Save will overwrite the existing rows in place.
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            </Paper>

            {/* Step 2 — Parameters */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid', borderColor: 'divider',
                bgcolor: 'background.paper',
                overflow: 'hidden',
              }}
            >
              <Box sx={{
                px: 2.5, py: 1.5,
                borderBottom: '1px solid', borderColor: 'divider',
                display: 'flex', alignItems: 'center', gap: 1.25,
                flexWrap: 'wrap',
              }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: 1.25,
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  color: 'white', fontWeight: 800, fontSize: '0.85rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 3px 8px rgba(17,153,142,0.32)',
                }}>
                  2
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: '0.95rem', fontWeight: 700, color: 'text.primary',
                    lineHeight: 1.2,
                  }}>
                    Parameters
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                    {formData.filter(p => p.parameterName.trim()).length}/{formData.length} ready
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                  onClick={addParameterRow}
                  sx={{
                    borderRadius: 1.75,
                    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: '0 3px 10px rgba(17,153,142,0.28)',
                    px: 1.75,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)',
                      boxShadow: '0 5px 14px rgba(17,153,142,0.4)'
                    }
                  }}
                >
                  Add Parameter
                </Button>
              </Box>

              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                {/* Parameter Cards */}
                <Stack spacing={1.75}>
                  {formData.map((param, index) => {
                    const isComplete = param.parameterName.trim().length > 0;
                    return (
                      <Paper
                        key={param.id}
                        elevation={0}
                        sx={{
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: isComplete ? 'rgba(56,239,125,0.40)' : 'divider',
                          bgcolor: 'background.paper',
                          overflow: 'hidden',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': {
                            borderColor: isComplete ? 'rgba(56,239,125,0.60)' : 'rgba(96,165,250,0.40)',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.16)'
                          },
                        }}
                      >
                        {/* Parameter header */}
                        <Box sx={{
                          px: 2, py: 1,
                          borderBottom: '1px solid', borderColor: 'divider',
                          display: 'flex', alignItems: 'center', gap: 1.25,
                          bgcolor: 'action.hover',
                        }}>
                          <Box sx={{
                            width: 4, height: 22, borderRadius: 0.5,
                            background: 'linear-gradient(180deg, #11998e 0%, #38ef7d 100%)',
                          }} />
                          <Typography sx={{
                            fontSize: '0.88rem', fontWeight: 700, color: 'text.primary',
                          }}>
                            Parameter {index + 1}
                          </Typography>
                          {/* Up/Down reorder controls. The dialog's array
                              order becomes the saved display_order on the
                              backend, so moving a row here is what changes
                              the order users see at result entry and on
                              the printed report. */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <Tooltip title={index === 0 ? 'Already at the top' : 'Move up'}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={index === 0}
                                  onClick={() => moveParameterUp(param.id)}
                                  sx={{
                                    width: 26, height: 26,
                                    color: index === 0 ? 'text.disabled' : '#11998e',
                                    '&:hover': { bgcolor: 'rgba(17,153,142,0.10)' },
                                  }}
                                >
                                  <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={index === formData.length - 1 ? 'Already at the bottom' : 'Move down'}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={index === formData.length - 1}
                                  onClick={() => moveParameterDown(param.id)}
                                  sx={{
                                    width: 26, height: 26,
                                    color: index === formData.length - 1 ? 'text.disabled' : '#11998e',
                                    '&:hover': { bgcolor: 'rgba(17,153,142,0.10)' },
                                  }}
                                >
                                  <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                          <Box sx={{ flexGrow: 1 }} />
                          {/* "Existing" badge on loaded rows so the user can
                              tell at a glance which rows came from the DB
                              vs which they added in this dialog session. */}
                          {param.trans_codes && param.trans_codes.length > 0 && (
                            <Chip
                              label="Existing"
                              size="small"
                              sx={{
                                height: 20, fontWeight: 700, fontSize: '0.65rem',
                                bgcolor: 'rgba(56,239,125,0.20)',
                                color: '#4ade80',
                                border: '1px solid rgba(74,222,128,0.40)',
                                '& .MuiChip-label': { px: 0.85 },
                              }}
                            />
                          )}
                          {/* Σ badge on dependent (derived) rows. */}
                          {param.type === 'dependent' && (
                            <Chip
                              label="Σ Derived"
                              size="small"
                              sx={{
                                height: 20, fontWeight: 700, fontSize: '0.65rem',
                                bgcolor: 'rgba(99,102,241,0.20)',
                                color: '#a5b4fc',
                                border: '1px solid rgba(165,180,252,0.40)',
                                '& .MuiChip-label': { px: 0.85 },
                              }}
                            />
                          )}
                          {isComplete ? (
                            <CheckCircleIcon sx={{ fontSize: 18, color: '#38ef7d' }} />
                          ) : (
                            <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                          )}
                          {param.parameterName.trim() && (
                            <Chip
                              label={param.parameterName.trim()}
                              size="small"
                              sx={{
                                height: 22, fontWeight: 700, fontSize: '0.7rem',
                                bgcolor: 'rgba(17,153,142,0.18)', color: '#4ade80',
                                border: '1px solid rgba(74,222,128,0.30)',
                                maxWidth: 220,
                                '& .MuiChip-label': { px: 0.85, overflow: 'hidden', textOverflow: 'ellipsis' },
                              }}
                            />
                          )}
                          {formData.length > 1 && (
                            <Tooltip title="Remove parameter" arrow>
                              <IconButton
                                size="small"
                                onClick={() => removeParameterRow(param.id)}
                                sx={{
                                  color: '#f87171',
                                  bgcolor: 'rgba(220,38,38,0.12)',
                                  '&:hover': { bgcolor: 'rgba(220,38,38,0.22)' },
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>

                        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                          {/* Single sequential grid — Name → Type → Ranges → Unit
                              → Alt Unit → Factor → Flags. Lets the user blaze
                              through one parameter and tab into the next.
                              The Alt Unit + Factor pair is for dual-unit
                              parameters (e.g. Glucose mg/dL ↔ mmol/L). Leave
                              both blank for single-unit parameters. */}
                          <Box sx={{
                            display: 'grid', gap: 1.25,
                            gridTemplateColumns: {
                              xs: '1fr',
                              sm: '1fr 1fr',
                              md: 'repeat(2, 1fr)',
                              lg: '1.7fr 1.1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1.3fr',
                            },
                          }}>
                            <TextField
                              fullWidth size="small"
                              label={labFormat === 'narrative' ? 'Section Title *' : 'Parameter Name *'}
                              value={param.parameterName}
                              onChange={(e) => handleInputChange(param.id, 'parameterName', e.target.value)}
                              placeholder={labFormat === 'narrative' ? 'e.g., WBC Morphology' : 'e.g., Hemoglobin'}
                              // In narrative mode the row collapses to just
                              // a section title — the range / unit / flag
                              // fields below don't apply.
                              sx={labFormat === 'narrative' ? { gridColumn: '1 / -1' } : undefined}
                            />
                            {labFormat === 'parameter' && (
                              <>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Type</InputLabel>
                                  <Select
                                    value={param.type || 'independent'}
                                    label="Type"
                                    onChange={(e) => {
                                      const next = e.target.value;
                                      // Toggle the isDerived flag in lock-step
                                      // with the Type pick so save logic and the
                                      // result-entry UI both work off a single
                                      // source of truth.
                                      setFormData(prev => prev.map(p => p.id === param.id ? {
                                        ...p,
                                        type: next,
                                        isDerived: next === 'dependent',
                                        formula: next === 'dependent' ? (p.formula || '') : '',
                                      } : p));
                                    }}
                                  >
                                    <MenuItem value="independent">Independent</MenuItem>
                                    <MenuItem value="dependent">Dependent (calculated)</MenuItem>
                                  </Select>
                                </FormControl>
                                <TextField
                                  fullWidth size="small"
                                  label="Male Range"
                                  value={param.maleRange}
                                  onChange={(e) => handleInputChange(param.id, 'maleRange', e.target.value)}
                                  placeholder="13.5-17.5"
                                />
                                <TextField
                                  fullWidth size="small"
                                  label="Female Range"
                                  value={param.femaleRange}
                                  onChange={(e) => handleInputChange(param.id, 'femaleRange', e.target.value)}
                                  placeholder="12.0-15.5"
                                />
                                <TextField
                                  fullWidth size="small"
                                  label="Children Range"
                                  value={param.childrenRange}
                                  onChange={(e) => handleInputChange(param.id, 'childrenRange', e.target.value)}
                                  placeholder="11.0-14.0"
                                />
                                <TextField
                                  fullWidth size="small"
                                  label="Neonate Range"
                                  value={param.neonateRange}
                                  onChange={(e) => handleInputChange(param.id, 'neonateRange', e.target.value)}
                                  placeholder="14.0-20.0"
                                />
                                <TextField
                                  fullWidth size="small"
                                  label="Unit *"
                                  value={param.unit}
                                  onChange={(e) => handleInputChange(param.id, 'unit', e.target.value)}
                                  placeholder="mg/dL"
                                />
                                <TextField
                                  fullWidth size="small"
                                  label="Alt Unit"
                                  value={param.altUnit}
                                  onChange={(e) => handleInputChange(param.id, 'altUnit', e.target.value)}
                                  placeholder="mmol/L"
                                />
                                <TextField
                                  fullWidth size="small"
                                  type="number"
                                  label="× Factor"
                                  value={param.conversionFactor}
                                  onChange={(e) => handleInputChange(param.id, 'conversionFactor', e.target.value)}
                                  placeholder="0.0555"
                                  inputProps={{ step: 'any', min: 0 }}
                                />
                                <TextField
                                  fullWidth size="small"
                                  label="Flags"
                                  value={param.flags}
                                  onChange={(e) => handleInputChange(param.id, 'flags', e.target.value)}
                                  placeholder="normal, high, low"
                                />
                              </>
                            )}
                          </Box>

                          {/* Formula editor — only rendered when Type is
                              "Dependent". The Formula field accepts any
                              arithmetic expression made of parameter names
                              (taken from the same lab) plus + - * / ^ and
                              parentheses. The chips below list every
                              independent parameter currently in the form,
                              clicking one inserts its name into the formula
                              at the caret. */}
                          {param.type === 'dependent' && (() => {
                            // Independents render first, then dependents,
                            // so the chip row keeps a natural reading order.
                            const peersInd = formData
                              .filter(p => p.id !== param.id && p.parameterName.trim() && p.type !== 'dependent')
                              .map(p => p.parameterName.trim());
                            const peersDep = formData
                              .filter(p => p.id !== param.id && p.parameterName.trim() && p.type === 'dependent')
                              .map(p => p.parameterName.trim());
                            const peers = [...peersInd, ...peersDep];

                            // Parse the formula to surface inputs detected
                            // and any syntax / cycle problems live.
                            const expr = (param.formula || '').trim();
                            let parseOk = true;
                            let parseMsg = '';
                            let inputs = [];
                            if (expr) {
                              try {
                                parseFormula(expr);
                                inputs = extractIdentifiers(expr);
                              } catch (err) {
                                parseOk = false;
                                parseMsg = err instanceof FormulaError ? err.message : 'Invalid formula';
                              }
                            }
                            const selfRef = inputs.some(n => n.toLowerCase() === param.parameterName.trim().toLowerCase());
                            const peerLcSet = new Set(peers.map(n => n.toLowerCase()));
                            const unknown = inputs.filter((n) => {
                              const lc = n.toLowerCase();
                              if (peerLcSet.has(lc)) return false;
                              if (lc === param.parameterName.trim().toLowerCase()) return false;
                              // Patient-context idents like `age` are
                              // injected at result-entry time and don't
                              // need to be declared as parameters.
                              if (PATIENT_CONTEXT_IDS.has(lc)) return false;
                              return true;
                            });

                            // Cycle detection across the lab — surfaces A→B→A
                            // patterns that the per-row "unknown" check can't
                            // see. Only the cycle that includes THIS row is
                            // reported, so adjacent rows don't all light up.
                            let cyclePath = null;
                            if (parseOk && unknown.length === 0 && !selfRef && expr) {
                              const cycles = findCycles(
                                formData
                                  .filter(p => p.parameterName.trim())
                                  .map(p => ({ name: p.parameterName.trim(), formula: p.formula || '' }))
                              );
                              const meLc = param.parameterName.trim().toLowerCase();
                              const hit = cycles.find(c => c.some(n => n === meLc));
                              if (hit) {
                                const byLc = new Map(
                                  formData
                                    .filter(p => p.parameterName.trim())
                                    .map(p => [p.parameterName.trim().toLowerCase(), p.parameterName.trim()])
                                );
                                cyclePath = hit.map(n => byLc.get(n) || n).join(' → ');
                              }
                            }

                            // Wrap any name that the bare-identifier rule
                            // can't accept (spaces, hyphens, parentheses,
                            // anything outside [A-Za-z0-9_./%#]) in [..]
                            // so the formula tokenizer reads it as a single
                            // identifier. Simple names like HGB stay bare
                            // for readability.
                            const bracketIfNeeded = (name) => (
                              /^[A-Za-z_][A-Za-z0-9_./%#]*$/.test(name) ? name : `[${name}]`
                            );
                            const insertAtEnd = (name) => {
                              const next = (param.formula || '').trim();
                              const joiner = next && !/[+\-*/^( ]$/.test(next) ? ' ' : '';
                              handleInputChange(param.id, 'formula', next + joiner + bracketIfNeeded(name));
                            };

                            return (
                              <Box sx={{
                                mt: 1.25, px: 1.25, py: 1, borderRadius: 1.5,
                                bgcolor: 'rgba(99,102,241,0.10)',
                                border: '1.5px solid rgba(99,102,241,0.40)',
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                  <Box sx={{
                                    fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase',
                                    letterSpacing: '0.8px', color: '#a5b4fc',
                                  }}>
                                    Σ Derived from formula
                                  </Box>
                                  <Box sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                                    Result is auto-computed at entry time — no manual input
                                  </Box>
                                </Box>
                                <TextField
                                  fullWidth size="small"
                                  label="Formula"
                                  value={param.formula || ''}
                                  onChange={(e) => handleInputChange(param.id, 'formula', e.target.value)}
                                  placeholder="(HGB / RBC) * 10   ·   or   [Total Chol] / [HDL]"
                                  error={(expr && !parseOk) || selfRef || unknown.length > 0 || !!cyclePath}
                                  helperText={
                                    !expr ? 'Use other parameter names — wrap names with spaces in [brackets]. Calculated parameters can reference other calculated parameters.'
                                    : !parseOk ? `Syntax error: ${parseMsg}`
                                    : selfRef ? `A formula cannot reference itself ("${param.parameterName.trim()}")`
                                    : unknown.length > 0 ? `Unknown parameter${unknown.length === 1 ? '' : 's'}: ${unknown.join(', ')}. Names with spaces must be wrapped in [brackets].`
                                    : cyclePath ? `Formula cycle detected: ${cyclePath}`
                                    : `Inputs detected: ${inputs.join(', ')}`
                                  }
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                      fontSize: '0.9rem',
                                    },
                                  }}
                                />
                                {peers.length > 0 && (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                                    <Box sx={{ fontSize: '0.7rem', color: 'text.secondary', mr: 0.5, alignSelf: 'center' }}>
                                      Insert:
                                    </Box>
                                    {peers.map((name) => {
                                      // Style dependents distinctly so the
                                      // user can see at a glance which chip
                                      // is a manual input and which is itself
                                      // a calculated value.
                                      const isDep = peersDep.includes(name);
                                      return (
                                        <Chip
                                          key={name}
                                          label={isDep ? `Σ ${name}` : name}
                                          size="small"
                                          onClick={() => insertAtEnd(name)}
                                          sx={{
                                            height: 22, fontSize: '0.7rem', fontWeight: 600,
                                            bgcolor: isDep ? 'rgba(34,197,94,0.16)' : 'rgba(165,180,252,0.16)',
                                            color:   isDep ? '#86efac' : '#a5b4fc',
                                            border:  `1px solid ${isDep ? 'rgba(134,239,172,0.35)' : 'rgba(165,180,252,0.35)'}`,
                                            cursor: 'pointer',
                                            '& .MuiChip-label': { px: 0.85 },
                                            '&:hover': {
                                              bgcolor: isDep ? 'rgba(34,197,94,0.28)' : 'rgba(165,180,252,0.28)',
                                            },
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>
                                )}
                              </Box>
                            );
                          })()}

                          {/* Live conversion preview — appears only when the
                              user fills both alt-unit fields. Renders the
                              reverse equation too so they can sanity-check
                              the factor before saving. */}
                          {(() => {
                            const altUnitText = (param.altUnit || '').trim();
                            const factorNum   = Number(param.conversionFactor);
                            const ready       = altUnitText && Number.isFinite(factorNum) && factorNum > 0;
                            if (!ready && !(altUnitText || param.conversionFactor)) return null;

                            const primary = (param.unit || '').trim() || 'unit';
                            const reverse = ready ? (1 / factorNum) : null;
                            const fmt = (n) => {
                              if (!Number.isFinite(n)) return '—';
                              if (n >= 100 || n <= 0.0001) return n.toExponential(4);
                              return Number(n.toFixed(6)).toString();
                            };

                            return (
                              <Box sx={{
                                mt: 1.25, px: 1.25, py: 0.9, borderRadius: 1.5,
                                bgcolor: ready ? 'rgba(17,153,142,0.10)' : 'rgba(245,158,11,0.12)',
                                border: '1px solid',
                                borderColor: ready ? 'rgba(56,239,125,0.30)' : 'rgba(245,158,11,0.35)',
                                display: 'flex', flexWrap: 'wrap', gap: 1.5,
                                alignItems: 'center',
                              }}>
                                <Box sx={{
                                  fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase',
                                  letterSpacing: '0.8px',
                                  color: ready ? '#4ade80' : '#f59e0b',
                                }}>
                                  {ready ? 'Conversion ready' : 'Fill both alt unit and factor'}
                                </Box>
                                {ready && (
                                  <>
                                    <Box sx={{ fontSize: '0.78rem', color: 'text.primary' }}>
                                      <strong>1 {primary}</strong>
                                      <span style={{ opacity: 0.55, margin: '0 6px' }}>×</span>
                                      <strong>{fmt(factorNum)}</strong>
                                      <span style={{ opacity: 0.55, margin: '0 6px' }}>=</span>
                                      <strong>{fmt(factorNum)} {altUnitText}</strong>
                                    </Box>
                                    <Box sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                                      <strong>1 {altUnitText}</strong>
                                      <span style={{ opacity: 0.55, margin: '0 6px' }}>×</span>
                                      <strong>{fmt(reverse)}</strong>
                                      <span style={{ opacity: 0.55, margin: '0 6px' }}>=</span>
                                      <strong>{fmt(reverse)} {primary}</strong>
                                    </Box>
                                  </>
                                )}
                              </Box>
                            );
                          })()}
                        </Box>
                      </Paper>
                    );
                  })}

                  {/* Inline "Add another parameter" — encourages batch entry by
                      sitting flush below the last row instead of being tucked
                      into the section header far above. */}
                  <Box
                    onClick={addParameterRow}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        addParameterRow();
                      }
                    }}
                    sx={{
                      mt: 1,
                      p: 1.75,
                      borderRadius: 2,
                      border: '1.5px dashed',
                      borderColor: 'rgba(56,239,125,0.40)',
                      bgcolor: 'rgba(17,153,142,0.06)',
                      color: '#4ade80',
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: 1, fontWeight: 700,
                      transition: 'all 0.18s ease',
                      '&:hover': {
                        bgcolor: 'rgba(17,153,142,0.12)',
                        borderColor: 'rgba(56,239,125,0.70)',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    <AddIcon sx={{ fontSize: 18 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>
                      Add another parameter
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>

            {/* ── Interpretation / Reference Table ──────────────────────── */}
            {selectedCategory && (() => {
              const catObj = categories.find(c => c.cat === selectedCategory);
              const hasInterp = !!(catObj?.interpretation);
              return (
                <Paper elevation={0} sx={{
                  p: 2, borderRadius: 2,
                  border: `1.5px solid ${hasInterp ? '#a5d6a7' : 'rgba(124,58,237,0.25)'}`,
                  background: hasInterp
                    ? 'linear-gradient(135deg,rgba(17,153,142,0.04) 0%,rgba(56,239,125,0.04) 100%)'
                    : 'linear-gradient(135deg,rgba(124,58,237,0.04) 0%,rgba(236,72,153,0.04) 100%)',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <TableChartIcon sx={{ fontSize: 22, color: hasInterp ? '#11998e' : '#9ca3af' }} />
                    <Box sx={{ flex: 1, minWidth: 150 }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary' }}>
                        Interpretation / Reference Table
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                        {hasInterp
                          ? 'A reference table is set — it will print below this lab\'s results.'
                          : 'No reference table yet. Generate with AI or set it manually.'}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={interpAiGenerating
                        ? <CircularProgress size={13} color="inherit" />
                        : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                      disabled={interpAiGenerating}
                      onClick={async () => {
                        // Generate directly without opening dialog
                        setInterpAiGenerating(true);
                        setInterpAiError('');
                        try {
                          const { data } = await axios.post(generateLabInterpretation, { labName: selectedCategory });
                          if (!data?.success) throw new Error(data?.message || 'AI returned no content.');
                          const html = data.html || '';
                          const cat = categories.find(c => c.cat === selectedCategory) || { cat: selectedCategory, trans_code: null, waiting_time: 0, interpretation: '' };
                          setInterpDialogCategory(cat);
                          setInterpDialogValue(html);
                          setInterpTableData(parseInterpHTML(html));
                          setInterpEditMode('visual');
                          setInterpAiError('');
                        } catch (err) {
                          setInterpAiError(err.response?.data?.message || err.message || 'AI generation failed.');
                        } finally {
                          setInterpAiGenerating(false);
                        }
                      }}
                      sx={{
                        textTransform: 'none', fontWeight: 700, fontSize: '0.75rem',
                        borderColor: '#7c3aed', color: '#7c3aed',
                        '&:hover': { borderColor: '#6d28d9', bgcolor: 'rgba(124,58,237,0.06)' },
                      }}
                    >
                      {interpAiGenerating ? 'Generating…' : 'AI Generate'}
                    </Button>
                    <Button
                      size="small"
                      variant={hasInterp ? 'outlined' : 'contained'}
                      startIcon={<TableChartIcon sx={{ fontSize: 14 }} />}
                      onClick={() => {
                        const cat = categories.find(c => c.cat === selectedCategory) || { cat: selectedCategory, trans_code: null, waiting_time: 0, interpretation: '' };
                        handleOpenInterpDialog(cat);
                      }}
                      sx={{
                        textTransform: 'none', fontWeight: 700, fontSize: '0.75rem',
                        ...(hasInterp
                          ? { borderColor: '#11998e', color: '#11998e', '&:hover': { borderColor: '#0e8679', bgcolor: 'rgba(17,153,142,0.06)' } }
                          : { background: 'linear-gradient(135deg,#11998e 0%,#38ef7d 100%)', '&:hover': { background: 'linear-gradient(135deg,#0e8679 0%,#2ed968 100%)' } }),
                      }}
                    >
                      {hasInterp ? 'Edit Table' : 'Set Table'}
                    </Button>
                  </Box>
                  {interpAiError && (
                    <Typography sx={{ fontSize: '0.75rem', color: '#d33', mt: 0.75 }}>{interpAiError}</Typography>
                  )}
                </Paper>
              );
            })()}
          </Stack>
        </DialogContent>

        <DialogActions sx={{
          px: 3, py: 2,
          borderTop: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper',
          gap: 1,
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
              {!selectedCategory
                ? 'Pick a category to start.'
                : formData.filter(p => p.parameterName.trim()).length === 0
                  ? 'Fill in at least one parameter name to save.'
                  : `Ready to save ${formData.filter(p => p.parameterName.trim()).length} parameter${formData.filter(p => p.parameterName.trim()).length === 1 ? '' : 's'}.`}
            </Typography>
          </Box>
          <Button
            onClick={handleCloseDialog}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.secondary',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!selectedCategory || formData.every(param => !param.parameterName.trim())}
            startIcon={<SaveIcon />}
            sx={{
              px: 2.5,
              textTransform: 'none',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              boxShadow: '0 4px 12px rgba(17,153,142,0.32)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)',
                boxShadow: '0 6px 16px rgba(17,153,142,0.4)',
              },
              '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: 'text.disabled' },
            }}
          >
            Save All Parameters ({formData.filter(param => param.parameterName.trim()).length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── AI Parameter Generation Dialog ─────────────────────────────────
          Sits inside Add Lab Parameters as a nested helper dialog. The user
          types a lab name (and optionally a few hints), the backend hits
          DeepSeek and returns a shaped parameter set, and we drop those
          rows into formData so the user can review and save. */}
      <Dialog
        open={aiDialogOpen}
        onClose={handleCloseAiDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            background: (theme) => theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
              : '#ffffff',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{
          px: 2.5, py: 2,
          background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
          color: 'white',
          display: 'flex', alignItems: 'center', gap: 1.25,
        }}>
          <Box sx={{
            width: 34, height: 34, borderRadius: 1.5,
            bgcolor: 'rgba(255,255,255,0.18)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AutoAwesomeIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, lineHeight: 1.2 }}>
              AI Lab Parameter Generator
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', opacity: 0.85, lineHeight: 1.2 }}>
              Type the lab name &mdash; the AI drafts every parameter
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleCloseAiDialog}
            disabled={aiGenerating}
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, pt: '24px !important', pb: 2.5 }}>
          <Stack spacing={2.25} sx={{ mt: 0.5 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Lab name *"
              placeholder="e.g. Thyroid Function Test, Lipid Profile, HbA1c"
              value={aiForm.labName}
              onChange={(e) => setAiForm((p) => ({ ...p, labName: e.target.value }))}
              disabled={aiGenerating}
              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ScienceIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <TextField
                fullWidth size="small"
                label="Specimen (optional)"
                placeholder="Whole blood, Serum, Urine…"
                value={aiForm.specimenType}
                onChange={(e) => setAiForm((p) => ({ ...p, specimenType: e.target.value }))}
                disabled={aiGenerating}
              />
              <TextField
                fullWidth size="small"
                label="Methodology (optional)"
                placeholder="ELISA, PCR, Spectrophotometry…"
                value={aiForm.methodology}
                onChange={(e) => setAiForm((p) => ({ ...p, methodology: e.target.value }))}
                disabled={aiGenerating}
              />
              <TextField
                fullWidth size="small"
                label="Age focus (optional)"
                placeholder="Adults, Paediatrics, Neonatal…"
                value={aiForm.ageFocus}
                onChange={(e) => setAiForm((p) => ({ ...p, ageFocus: e.target.value }))}
                disabled={aiGenerating}
              />
              <TextField
                fullWidth size="small"
                label="Region (optional)"
                placeholder="Ghana / West Africa"
                value={aiForm.region}
                onChange={(e) => setAiForm((p) => ({ ...p, region: e.target.value }))}
                disabled={aiGenerating}
              />
            </Box>

            <TextField
              fullWidth size="small"
              label="Extra notes for the AI (optional)"
              placeholder="e.g. include MCV/MCH/MCHC, use SI units, conventional ranges only…"
              value={aiForm.extraNotes}
              onChange={(e) => setAiForm((p) => ({ ...p, extraNotes: e.target.value }))}
              disabled={aiGenerating}
              multiline minRows={2} maxRows={4}
            />

            {aiError && (
              <Box sx={{
                p: 1.25, borderRadius: 1.5,
                bgcolor: 'rgba(239,68,68,0.10)',
                border: '1px solid rgba(239,68,68,0.35)',
                color: '#ef4444',
                fontSize: '0.82rem', fontWeight: 600,
              }}>
                {aiError}
              </Box>
            )}

            <Box sx={{
              p: 1.25, borderRadius: 1.5,
              bgcolor: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(124,58,237,0.10)' : 'rgba(124,58,237,0.06)',
              border: '1px dashed rgba(124,58,237,0.35)',
              fontSize: '0.74rem', color: 'text.secondary',
              lineHeight: 1.5,
            }}>
              The AI will draft parameter names, reference ranges, units, alt-units (with
              conversion factors), and derived calculations like MCV or LDL where they apply.
              You can edit anything afterwards before saving.
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1 }}>
          <Button
            onClick={handleCloseAiDialog}
            disabled={aiGenerating}
            sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerateWithAi}
            disabled={aiGenerating || !aiForm.labName.trim()}
            variant="contained"
            startIcon={aiGenerating
              ? <CircularProgress size={16} sx={{ color: 'white' }} />
              : <AutoAwesomeIcon sx={{ fontSize: 18 }} />}
            sx={{
              px: 2.5,
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 1.75,
              background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
              boxShadow: '0 4px 12px rgba(124,58,237,0.32)',
              '&:hover': {
                background: 'linear-gradient(135deg, #6d28d9 0%, #db2777 100%)',
                boxShadow: '0 6px 16px rgba(124,58,237,0.4)',
              },
              '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: 'text.disabled' },
            }}
          >
            {aiGenerating ? 'Generating…' : 'Generate Parameters'}
          </Button>
        </DialogActions>
      </Dialog>

   {/* Add Lab Category Dialog */}
      
          
    <Dialog
      open={openCategoryDialog}
      onClose={handleCloseCategoryDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            color: 'white',
            margin: -3,
            marginBottom: 2,
            borderRadius: '12px 12px 0 0',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            <CategoryIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Add Lab Category
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Create a new laboratory test category
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ bgcolor: '#f1fcf9', p: 3 }}>
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid #c8e6c9',
              bgcolor: 'background.paper'
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: '#1b5e20',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: '#11998e'
                }}
              />
              Category Information
            </Typography>
            
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label="Category Name *"
                value={categoryFormData.categoryName}
                onChange={(e) => handleCategoryInputChange('categoryName', e.target.value)}
                placeholder="e.g., Hematology, Biochemistry, Microbiology"
                helperText="Enter a descriptive name for the new lab category"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#11998e',
                        borderWidth: 2
                      }
                    }
                  },
                  '& .MuiFormLabel-root.Mui-focused': {
                    color: '#11998e'
                  }
                }}
              />

              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Interpretation / Reference Table (optional)"
                value={categoryFormData.interpretation}
                onChange={(e) => handleCategoryInputChange('interpretation', e.target.value)}
                placeholder="e.g. Clinical interpretation notes or an HTML table that prints below the results on every report."
                helperText="Printed below the results table on the lab report. Accepts plain text or HTML."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#11998e', borderWidth: 2 }
                    }
                  },
                  '& .MuiFormLabel-root.Mui-focused': { color: '#11998e' }
                }}
              />

              <TextField
                fullWidth
                type="number"
                label="Waiting Time (Days)"
                value={categoryFormData.waitingTime}
                onChange={(e) => handleCategoryInputChange('waitingTime', e.target.value)}
                placeholder="e.g., 2, 3, 5"
                helperText="Expected turnaround time for test results in days"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTime sx={{ color: '#11998e' }} />
                    </InputAdornment>
                  ),
                  inputProps: { min: 0 }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#11998e',
                        borderWidth: 2
                      }
                    }
                  },
                  '& .MuiFormLabel-root.Mui-focused': {
                    color: '#11998e'
                  }
                }}
              />
            </Stack>
          </Paper>

          {categories.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #c8e6c9',
                bgcolor: 'background.paper'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="body2" fontWeight={700} color="white">
                    {categories.length}
                  </Typography>
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1b5e20' }}>
                  Existing Categories
                </Typography>
              </Box>
              
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  p: 2,
                  bgcolor: '#f1fcf9',
                  borderRadius: 2,
                  border: '1px solid #e8f5e9',
                  maxHeight: 200,
                  overflowY: 'auto'
                }}
              >
                {categories.map((category) => (
                  <Box
                    key={category.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      bgcolor: 'background.paper',
                      borderRadius: 1.5,
                      border: '1px solid #c8e6c9',
                      '&:hover': {
                        borderColor: '#11998e',
                        boxShadow: '0 2px 8px rgba(17, 153, 142, 0.1)'
                      }
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} color="#1b5e20">
                      {category.cat}
                    </Typography>
                    <Chip
                      icon={<AccessTime sx={{ fontSize: 14 }} />}
                      label={`${category.waiting_time || 0} days`}
                      size="small"
                      sx={{
                        bgcolor: (category.waiting_time || 0) > 3 ? '#ffebee' : '#e8f5e9',
                        color: (category.waiting_time || 0) > 3 ? '#c62828' : '#2e7d32',
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, bgcolor: '#f1fcf9', gap: 2 }}>
        <Button
          onClick={handleCloseCategoryDialog}
          sx={{
            px: 3,
            py: 1,
            borderRadius: 2,
            color: '#666',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSaveCategory}
          variant="contained"
          disabled={!categoryFormData.categoryName.trim()}
          sx={{
            px: 4,
            py: 1,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)',
              boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)'
            },
            '&:disabled': {
              background: '#e0e0e0',
              color: '#9e9e9e'
            }
          }}
        >
          💾 Save Category
        </Button>
      </DialogActions>
    </Dialog>

      {/* Edit Lab Category Dialog */}
  <Dialog
  open={editingCategory !== null}
  onClose={handleCloseEditDialog}
  maxWidth="sm"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: 3,
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
    }
  }}
>
  <DialogTitle>
    <Box
      sx={{
        p: 2,
        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        color: 'white',
        margin: -3,
        marginBottom: 2,
        borderRadius: '12px 12px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          bgcolor: 'rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(255, 255, 255, 0.3)'
        }}
      >
        <CategoryIcon sx={{ fontSize: 28 }} />
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          Edit Lab Category
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          Update laboratory test category details
        </Typography>
      </Box>
    </Box>
  </DialogTitle>
  
  <DialogContent sx={{ bgcolor: '#f1fcf9', p: 3 }}>
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid #c8e6c9',
          bgcolor: 'background.paper'
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: '#1b5e20',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: '#11998e'
            }}
          />
          Category Information
        </Typography>
        
        <Stack spacing={2.5}>
          <TextField
            fullWidth
            label="Category Name *"
            value={editCategoryFormData.categoryName}
            onChange={(e) => setEditCategoryFormData(prev => ({
              ...prev,
              categoryName: e.target.value
            }))}
            placeholder="e.g., Hematology, Biochemistry, Microbiology"
            helperText="Updating this category will also update all lab parameters with the same category name"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#11998e',
                    borderWidth: 2
                  }
                }
              },
              '& .MuiFormLabel-root.Mui-focused': {
                color: '#11998e'
              }
            }}
          />

          <TextField
            fullWidth
            type="number"
            label="Waiting Time (Days)"
            value={editCategoryFormData.waitingTime}
            onChange={(e) => setEditCategoryFormData(prev => ({
              ...prev,
              waitingTime: e.target.value
            }))}
            placeholder="e.g., 2, 3, 5"
            helperText="Expected turnaround time for test results in days"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccessTime sx={{ color: '#11998e' }} />
                </InputAdornment>
              ),
              inputProps: { min: 0 }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#11998e',
                    borderWidth: 2
                  }
                }
              },
              '& .MuiFormLabel-root.Mui-focused': {
                color: '#11998e'
              }
            }}
          />

          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Interpretation / Reference Table (optional)"
            value={editCategoryFormData.interpretation}
            onChange={(e) => setEditCategoryFormData(prev => ({ ...prev, interpretation: e.target.value }))}
            placeholder="e.g. Clinical interpretation notes or an HTML table that prints below the results on every report."
            helperText="Printed below the results table on the lab report. Accepts plain text or HTML."
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#11998e', borderWidth: 2 }
                }
              },
              '& .MuiFormLabel-root.Mui-focused': { color: '#11998e' }
            }}
          />
        </Stack>
      </Paper>

      {/* Warning Box */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: '1px solid #fff3cd',
          background: '#fff9e6'
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Typography fontSize={16}>⚠️</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e65100', mb: 0.5 }}>
              Update Notice
            </Typography>
            <Typography variant="body2" sx={{ color: '#ef6c00', lineHeight: 1.6 }}>
              Changes to this category will affect all associated lab parameters and test results.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Stack>
  </DialogContent>
  
  <DialogActions sx={{ p: 3, bgcolor: '#f1fcf9', gap: 2 }}>
    <Button
      onClick={handleCloseEditDialog}
      sx={{
        px: 3,
        py: 1,
        borderRadius: 2,
        color: '#666',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
          bgcolor: 'rgba(0, 0, 0, 0.04)'
        }
      }}
    >
      Cancel
    </Button>
    <Button
      onClick={handleUpdateCategory}
      variant="contained"
      disabled={
        !editCategoryFormData.categoryName.trim() ||
        (editCategoryFormData.categoryName.trim() === editingCategory?.cat &&
         editCategoryFormData.waitingTime === editingCategory?.waiting_time &&
         (editCategoryFormData.interpretation || '') === (editingCategory?.interpretation || ''))
      }
      sx={{
        px: 4,
        py: 1,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        fontWeight: 600,
        textTransform: 'none',
        boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
        '&:hover': {
          background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)',
          boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)'
        },
        '&:disabled': {
          background: '#e0e0e0',
          color: '#9e9e9e'
        }
      }}
    >
      ✏️ Update Category
    </Button>
  </DialogActions>
      </Dialog>

      {/* ── Interpretation / Reference Table Dialog ─────────────────── */}
      <Dialog
        open={interpDialogCategory !== null}
        onClose={handleCloseInterpDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{
            p: 2,
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            color: 'white',
            borderRadius: '12px 12px 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TableChartIcon sx={{ fontSize: 26 }} />
              <Box>
                <Typography variant="h6" fontWeight={700}>Interpretation / Reference Table</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {interpDialogCategory?.cat} — printed below results on every report
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseInterpDialog} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5 }}>

          {/* ── Top bar: mode tabs + AI generate ─────────────── */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Mode switcher */}
            <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
              {[
                { key: 'visual', label: 'Edit Table', icon: <TableChartIcon sx={{ fontSize: 15 }} /> },
                { key: 'html',   label: 'HTML',       icon: <CodeIcon       sx={{ fontSize: 15 }} /> },
                { key: 'preview',label: 'Preview',    icon: <VisibilityIcon sx={{ fontSize: 15 }} /> },
              ].map(({ key, label, icon }) => (
                <Button
                  key={key}
                  size="small"
                  onClick={() => handleSwitchInterpMode(key)}
                  startIcon={icon}
                  sx={{
                    textTransform: 'none', fontWeight: 600, fontSize: '0.75rem',
                    borderRadius: 0, px: 1.5, py: 0.6,
                    bgcolor: interpEditMode === key ? '#11998e' : 'transparent',
                    color: interpEditMode === key ? 'white' : 'text.secondary',
                    '&:hover': { bgcolor: interpEditMode === key ? '#0e8679' : 'action.hover' },
                  }}
                >
                  {label}
                </Button>
              ))}
            </Box>

            {/* AI generate */}
            <Button
              size="small"
              variant="outlined"
              disabled={interpAiGenerating}
              onClick={() => handleGenerateInterpWithAi(interpDialogCategory?.cat)}
              startIcon={interpAiGenerating
                ? <CircularProgress size={12} color="inherit" />
                : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
              sx={{
                textTransform: 'none', fontWeight: 700, fontSize: '0.75rem',
                borderColor: '#7c3aed', color: '#7c3aed',
                '&:hover': { borderColor: '#6d28d9', bgcolor: 'rgba(124,58,237,0.06)' },
                '&:disabled': { borderColor: '#ccc', color: '#aaa' },
              }}
            >
              {interpAiGenerating ? 'Generating…' : 'AI Generate'}
            </Button>
          </Box>

          {interpAiError && (
            <Typography sx={{ fontSize: '0.78rem', color: '#d33', mb: 1 }}>{interpAiError}</Typography>
          )}

          {/* ── VISUAL editor ─────────────────────────────────── */}
          {interpEditMode === 'visual' && (
            <Box>
              {/* Comment / note field */}
              <TextField
                fullWidth size="small" multiline minRows={2}
                label="Comments / Note (each line prints as a separate line above the table)"
                placeholder="e.g. Anti-HBc IgM is required to differentiate acute from chronic&#10;Line 2 of comments..."
                value={interpTableData.comment}
                onChange={e => setInterpTableData(prev => ({ ...prev, comment: e.target.value }))}
                sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#11998e' } }, '& label.Mui-focused': { color: '#11998e' } }}
              />

              {interpTableData.headers.length === 0 ? (
                <Box sx={{ border: '1.5px dashed #ccc', borderRadius: 2, p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  <TableChartIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1 }} />
                  <Typography sx={{ fontSize: '0.85rem' }}>No table yet. Click <b>AI Generate</b> or <b>+ Add Column</b> to start.</Typography>
                  <Button size="small" variant="outlined" sx={{ mt: 1.5, textTransform: 'none', borderColor: '#11998e', color: '#11998e' }}
                    onClick={() => setInterpTableData({ comment: interpTableData.comment, headers: ['Column 1'], rows: [['Value']] })}>
                    + Start Blank Table
                  </Button>
                </Box>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ '& td, & th': { border: '1px solid', borderColor: 'divider', p: 0 } }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'rgba(17,153,142,0.06)' }}>
                        {interpTableData.headers.map((h, ci) => (
                          <TableCell key={ci} sx={{ minWidth: 100 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <TextField
                                size="small" variant="standard" value={h} fullWidth
                                inputProps={{ style: { fontWeight: 700, fontSize: '0.78rem', textAlign: 'center', padding: '4px 6px' } }}
                                InputProps={{ disableUnderline: true }}
                                onChange={e => setInterpTableData(prev => {
                                  const headers = [...prev.headers];
                                  headers[ci] = e.target.value;
                                  return { ...prev, headers };
                                })}
                              />
                              <IconButton size="small" sx={{ p: 0.25, color: '#ef4444', flexShrink: 0 }}
                                onClick={() => setInterpTableData(prev => ({
                                  ...prev,
                                  headers: prev.headers.filter((_, i) => i !== ci),
                                  rows: prev.rows.map(r => r.filter((_, i) => i !== ci)),
                                }))}>
                                <RemoveRowIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                        ))}
                        <TableCell sx={{ width: 32, p: '2px !important' }}>
                          <Tooltip title="Add column">
                            <IconButton size="small" sx={{ color: '#11998e' }}
                              onClick={() => setInterpTableData(prev => ({
                                ...prev,
                                headers: [...prev.headers, `Col ${prev.headers.length + 1}`],
                                rows: prev.rows.map(r => [...r, '']),
                              }))}>
                              <AddRowIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {interpTableData.rows.map((row, ri) => (
                        <TableRow key={ri} hover>
                          {interpTableData.headers.map((_, ci) => (
                            <TableCell key={ci}>
                              <TextField
                                size="small" variant="standard" fullWidth
                                value={row[ci] ?? ''}
                                inputProps={{ style: { fontSize: '0.78rem', textAlign: 'center', padding: '3px 6px' } }}
                                InputProps={{ disableUnderline: true }}
                                onChange={e => setInterpTableData(prev => {
                                  const rows = prev.rows.map((r, i) => i === ri ? r.map((c, j) => j === ci ? e.target.value : c) : r);
                                  return { ...prev, rows };
                                })}
                              />
                            </TableCell>
                          ))}
                          <TableCell sx={{ width: 32, p: '2px !important' }}>
                            <Tooltip title="Delete row">
                              <IconButton size="small" sx={{ color: '#ef4444' }}
                                onClick={() => setInterpTableData(prev => ({ ...prev, rows: prev.rows.filter((_, i) => i !== ri) }))}>
                                <RemoveRowIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Button size="small" startIcon={<AddRowIcon />} sx={{ mt: 1, textTransform: 'none', color: '#11998e', fontSize: '0.75rem' }}
                    onClick={() => setInterpTableData(prev => ({ ...prev, rows: [...prev.rows, Array(prev.headers.length).fill('')] }))}>
                    Add Row
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* ── HTML editor ───────────────────────────────────── */}
          {interpEditMode === 'html' && (
            <TextField
              fullWidth multiline minRows={12}
              value={interpDialogValue}
              onChange={e => setInterpDialogValue(e.target.value)}
              placeholder="Paste or type raw HTML here."
              sx={{ fontFamily: 'monospace', fontSize: '0.78rem', '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#11998e' } } }}
            />
          )}

          {/* ── Preview ───────────────────────────────────────── */}
          {interpEditMode === 'preview' && (() => {
            const previewHtml = interpTableData.headers.length > 0 ? buildInterpHTML(interpTableData) : interpDialogValue;
            return (
              <Box sx={{ border: '1px solid #ccc', borderRadius: 2, overflow: 'hidden', minHeight: 200 }}>
                <Box sx={{
                  p: 2, bgcolor: '#ffffff', color: '#111111', fontSize: '0.85rem',
                  '& table': { width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' },
                  '& th, & td': { border: '1px solid #aaa !important', padding: '4px 8px !important', textAlign: 'center !important', color: '#111 !important' },
                  '& th': { backgroundColor: '#dce6f5 !important', fontWeight: '700 !important' },
                  '& td': { backgroundColor: '#ffffff !important' },
                  '& p, & em, & span': { color: '#333 !important' },
                }}
                  dangerouslySetInnerHTML={{ __html: previewHtml || '<p style="color:#aaa">Nothing to preview yet.</p>' }}
                />
              </Box>
            );
          })()}

        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1.5 }}>
          <Button
            onClick={() => {
              setInterpTableData({ comment: '', headers: [], rows: [] });
              setInterpDialogValue('');
            }}
            sx={{ color: '#d33', textTransform: 'none' }}
          >
            Clear
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleCloseInterpDialog} sx={{ textTransform: 'none', color: '#666' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveInterp}
            variant="contained"
            disabled={interpDialogSaving}
            startIcon={interpDialogSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            sx={{
              textTransform: 'none', fontWeight: 600,
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)' },
              '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' },
            }}
          >
            {interpDialogSaving ? 'Saving…' : 'Save Table'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Lab Parameter Dialog */}
      <Dialog
        open={editingParameter !== null}
        onClose={handleCloseEditParameterDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Lab Parameter
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <div className="row">
              <div className="col-12 col-md-6">
                <TextField
                  fullWidth
                  label="Parameter Name *"
                  value={editParameterFormData.parameterName}
                  onChange={(e) => setEditParameterFormData(prev => ({
                    ...prev,
                    parameterName: e.target.value
                  }))}
                  placeholder="e.g., Hemoglobin, Glucose, WBC"
                  sx={{ mb: 2 }}
                />
              </div>
              <div className="col-12 col-md-6">
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={editParameterFormData.type}
                    label="Type"
                    onChange={(e) => setEditParameterFormData(prev => ({
                      ...prev,
                      type: e.target.value
                    }))}
                  >
                    <MenuItem value="independent">Independent</MenuItem>
                    <MenuItem value="dependent">Dependent</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>

            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
              Reference Ranges
            </Typography>
            <div className="row">
              <div className="col-12 col-md-6 col-lg-3">
                <TextField
                  fullWidth
                  label="Male Range"
                  value={editParameterFormData.maleRange}
                  onChange={(e) => setEditParameterFormData(prev => ({
                    ...prev,
                    maleRange: e.target.value
                  }))}
                  placeholder="e.g., 13.5-17.5"
                  sx={{ mb: 2 }}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <TextField
                  fullWidth
                  label="Female Range"
                  value={editParameterFormData.femaleRange}
                  onChange={(e) => setEditParameterFormData(prev => ({
                    ...prev,
                    femaleRange: e.target.value
                  }))}
                  placeholder="e.g., 12.0-15.5"
                  sx={{ mb: 2 }}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <TextField
                  fullWidth
                  label="Children Range"
                  value={editParameterFormData.childrenRange}
                  onChange={(e) => setEditParameterFormData(prev => ({
                    ...prev,
                    childrenRange: e.target.value
                  }))}
                  placeholder="e.g., 11.0-14.0"
                  sx={{ mb: 2 }}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <TextField
                  fullWidth
                  label="Neonate Range"
                  value={editParameterFormData.neonateRange}
                  onChange={(e) => setEditParameterFormData(prev => ({
                    ...prev,
                    neonateRange: e.target.value
                  }))}
                  placeholder="e.g., 14.0-20.0"
                  sx={{ mb: 2 }}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-12 col-md-6">
                <TextField
                  fullWidth
                  label="Unit *"
                  value={editParameterFormData.unit}
                  onChange={(e) => setEditParameterFormData(prev => ({
                    ...prev,
                    unit: e.target.value
                  }))}
                  placeholder="e.g., mg/dL, g/dL"
                  sx={{ mb: 2 }}
                />
              </div>
              <div className="col-12 col-md-6">
                <TextField
                  fullWidth
                  label="Flags (comma-separated)"
                  value={editParameterFormData.flags}
                  onChange={(e) => setEditParameterFormData(prev => ({
                    ...prev,
                    flags: e.target.value
                  }))}
                  placeholder="e.g., normal, high, low, positive, negative"
                  helperText="Enter possible result flags separated by commas"
                  sx={{ mb: 2 }}
                />
              </div>
            </div>

            {/* Dual-unit pair — leave both blank for single-unit parameters. */}
            <div className="row">
              <div className="col-12 col-md-6">
                <TextField
                  fullWidth
                  label="Alt Unit"
                  value={editParameterFormData.altUnit}
                  onChange={(e) => setEditParameterFormData(prev => ({
                    ...prev,
                    altUnit: e.target.value
                  }))}
                  placeholder="e.g., mmol/L, µmol/L"
                  helperText="Optional alternate unit"
                  sx={{ mb: 2 }}
                />
              </div>
              <div className="col-12 col-md-6">
                <TextField
                  fullWidth
                  type="number"
                  label="× Conversion Factor"
                  value={editParameterFormData.conversionFactor}
                  onChange={(e) => setEditParameterFormData(prev => ({
                    ...prev,
                    conversionFactor: e.target.value
                  }))}
                  placeholder="e.g., 0.0555"
                  helperText="value_in_alt = value_in_primary × factor"
                  inputProps={{ step: 'any', min: 0 }}
                  sx={{ mb: 2 }}
                />
              </div>
            </div>

            {/* Type + Formula — Independent is the default; pick
                Dependent and the Formula input appears. */}
            <div className="row">
              <div className="col-12 col-md-6">
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={editParameterFormData.type || 'independent'}
                    label="Type"
                    onChange={(e) => {
                      const next = e.target.value;
                      setEditParameterFormData(prev => ({
                        ...prev,
                        type: next,
                        isDerived: next === 'dependent',
                        formula: next === 'dependent' ? (prev.formula || '') : '',
                      }));
                    }}
                  >
                    <MenuItem value="independent">Independent</MenuItem>
                    <MenuItem value="dependent">Dependent (calculated)</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div className="col-12 col-md-6">
                {editParameterFormData.type === 'dependent' && (() => {
                  const expr = (editParameterFormData.formula || '').trim();
                  let parseOk = true; let parseMsg = ''; let inputs = [];
                  if (expr) {
                    try {
                      parseFormula(expr);
                      inputs = extractIdentifiers(expr);
                    } catch (err) {
                      parseOk = false;
                      parseMsg = err instanceof FormulaError ? err.message : 'Invalid formula';
                    }
                  }
                  return (
                    <TextField
                      fullWidth size="small"
                      label="Formula"
                      value={editParameterFormData.formula || ''}
                      onChange={(e) => setEditParameterFormData(prev => ({ ...prev, formula: e.target.value }))}
                      placeholder="(HGB / RBC) * 10   ·   or   [Total Chol] / [HDL]"
                      error={expr && !parseOk}
                      helperText={
                        !expr ? 'Use other parameter names — wrap names with spaces in [brackets], e.g. (HGB / RBC) * 10 or [Total Chol] / [HDL]'
                        : !parseOk ? `Syntax error: ${parseMsg}`
                        : `Inputs: ${inputs.join(', ')}`
                      }
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          fontSize: '0.9rem',
                        },
                      }}
                    />
                  );
                })()}
              </div>
            </div>

            {(() => {
              const altUnitText = (editParameterFormData.altUnit || '').trim();
              const factorNum   = Number(editParameterFormData.conversionFactor);
              const ready       = altUnitText && Number.isFinite(factorNum) && factorNum > 0;
              if (!ready) return null;

              const primary = (editParameterFormData.unit || '').trim() || 'unit';
              const reverse = 1 / factorNum;
              const fmt = (n) => {
                if (!Number.isFinite(n)) return '—';
                if (n >= 100 || n <= 0.0001) return n.toExponential(4);
                return Number(n.toFixed(6)).toString();
              };

              return (
                <Box sx={{
                  px: 1.5, py: 1, borderRadius: 1.5,
                  bgcolor: 'rgba(17,153,142,0.10)',
                  border: '1px solid rgba(56,239,125,0.30)',
                  mb: 2,
                }}>
                  <Box sx={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.8px', color: '#4ade80', mb: 0.5 }}>
                    Conversion preview
                  </Box>
                  <Box sx={{ fontSize: '0.82rem', color: 'text.primary', lineHeight: 1.6 }}>
                    1 {primary} × {fmt(factorNum)} = {fmt(factorNum)} {altUnitText}<br />
                    1 {altUnitText} × {fmt(reverse)} = {fmt(reverse)} {primary}
                  </Box>
                </Box>
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseEditParameterDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleUpdateParameter}
            variant="contained"
            disabled={!editParameterFormData.parameterName.trim()}
          >
            Update Parameter
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Lab preview dialog ─────────────────────────────────────────────
          Opens from the eye icon on every lab row. Builds a fake report
          from the lab's parameters (midpoint values for numeric ranges,
          "Negative" for qualitative tests, computed values for derived
          parameters) and renders both the standard and branded layouts
          inside an iframe so the user can see exactly how their setup
          will look on a real printed report. */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '92vh' } }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <VisibilityIcon sx={{ color: '#5468FF' }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
              Report Preview — {previewGroup?.hold_show || ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Sample values only. Not a real result. Use this to confirm the layout and parameter coverage.
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.secondary' }}>
              Layout:
            </Typography>
            <Button
              size="small"
              variant={previewTemplate === 'branded' ? 'contained' : 'outlined'}
              onClick={() => handleChangePreviewTemplate('branded')}
              sx={{ textTransform: 'none', borderRadius: 1.5 }}
            >
              Branded (Dekyi)
            </Button>
            <Button
              size="small"
              variant={previewTemplate === 'standard' ? 'contained' : 'outlined'}
              onClick={() => handleChangePreviewTemplate('standard')}
              sx={{ textTransform: 'none', borderRadius: 1.5 }}
            >
              Standard
            </Button>
            <Box sx={{ flex: 1 }} />
            <Chip
              size="small"
              label="PREVIEW · SAMPLE DATA"
              sx={{
                fontWeight: 700, fontSize: '0.65rem', letterSpacing: '.6px',
                bgcolor: 'rgba(255,193,7,0.18)', color: '#a07408',
                border: '1px solid rgba(255,193,7,0.5)',
              }}
            />
          </Box>
          <Box sx={{ flex: 1, bgcolor: '#f5f5f5' }}>
            <iframe
              title="Lab report preview"
              srcDoc={previewHtml}
              style={{ width: '100%', height: '100%', border: 'none', backgroundColor: 'white' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25 }}>
          <Button onClick={handleClosePreview} color="inherit">Close</Button>
          <Button onClick={handleOpenPreviewInWindow} variant="outlined">
            Open in new window
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Lab pricing info dialog ─────────────────────────────────────
          Read-only price view per lab, plus a "Request change" button
          that opens the nested form. The lab desk does not write to
          `lab_prices` directly — change requests land in the admin
          queue for review. */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 800, borderBottom: '1px solid', borderColor: 'divider' }}>
          <InfoIcon sx={{ color: '#0ea5e9' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>Lab pricing details</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.25 }}>{infoLabName}</Typography>
          </Box>
          <IconButton onClick={() => setInfoDialogOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {infoLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : infoRows.length === 0 ? (
            <Box sx={{
              textAlign: 'center', py: 5, px: 2,
              border: '1px dashed', borderColor: 'divider', borderRadius: 2,
              bgcolor: 'rgba(245,158,11,0.06)',
            }}>
              <MoneyIcon sx={{ fontSize: 36, color: '#f59e0b', mb: 1 }} />
              <Typography sx={{ fontWeight: 800 }}>No price set yet</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mt: 0.5 }}>
                This lab has no pricing rows on file. Use "Request change" below to ask admin to set one.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Insurance</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Tariff</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Top-up</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Uninsured</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>G-DRG</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Request</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {infoRows.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{r.status || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                        {Number(r.cost) ? Number(r.cost).toFixed(2) : (
                          <Chip size="small" label="not set" sx={{ height: 18, fontSize: '0.62rem', bgcolor: 'rgba(245,158,11,0.18)', color: '#f59e0b', fontWeight: 700 }} />
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                        {Number(r.toup) ? Number(r.toup).toFixed(2) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                        {Number(r.unin) ? Number(r.unin).toFixed(2) : '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{r.G_DRG || '—'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Request a change to this price">
                          <IconButton size="small" onClick={() => openRequestChange(r)} sx={{ color: '#f59e0b' }}>
                            <PriceChangeIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>
              Read-only view — changes go through admin via the request form.
            </Typography>
          </Box>
          <Button
            onClick={() => openRequestChange(null)}
            startIcon={<PriceChangeIcon />}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
          >
            Request change
          </Button>
          <Button onClick={() => setInfoDialogOpen(false)} color="inherit" sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Request price change dialog ─────────────────────────────── */}
      <Dialog
        open={reqOpen}
        onClose={() => { if (!reqSaving) setReqOpen(false); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, borderBottom: '1px solid', borderColor: 'divider' }}>
          Request price change
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Lab
              </Typography>
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, mt: 0.25 }}>{infoLabName}</Typography>
            </Box>
            <FormControl size="small" fullWidth>
              <InputLabel>Insurance partner</InputLabel>
              <Select
                label="Insurance partner"
                value={reqInsurance}
                onChange={(e) => {
                  const v = e.target.value;
                  setReqInsurance(v);
                  // If the picked partner already has a row, prime the
                  // current cost + proposed amount from it so the tech
                  // doesn't have to retype it.
                  const row = infoRows.find((r) => (r.status || '') === v);
                  if (row) {
                    setReqCurrent(Number(row.cost));
                    if (!reqAmount) setReqAmount(String(row.cost ?? ''));
                  } else {
                    setReqCurrent(null);
                  }
                }}
              >
                <MenuItem value=""><em>— Uninsured / general —</em></MenuItem>
                {insurancePartners.map((ip) => (
                  <MenuItem key={ip.name || ip.id} value={ip.name}>{ip.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              fullWidth
              label="Current cost on file"
              value={reqCurrent != null && Number.isFinite(Number(reqCurrent)) ? Number(reqCurrent).toFixed(2) : '—'}
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
            />
            <TextField
              size="small"
              fullWidth
              type="number"
              label="Proposed new cost *"
              value={reqAmount}
              onChange={(e) => setReqAmount(e.target.value)}
              inputProps={{ step: '0.01', min: '0' }}
            />
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              label="Reason (optional)"
              placeholder="e.g. NHIA tariff updated, mismatch with rate card…"
              value={reqReason}
              onChange={(e) => setReqReason(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setReqOpen(false)} disabled={reqSaving} color="inherit" sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={submitChangeRequest}
            disabled={reqSaving || !reqAmount}
            startIcon={reqSaving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <PriceChangeIcon />}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Submit request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

};

export default Home;